import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack5';
import {
  Box,
  Chip,
  Grid,
  Stack,
  Alert,
  Button,
  Dialog,
  Slider,
  Divider,
  Checkbox,
  Collapse,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  ButtonBase,
  FormControl,
  ListSubheader,
  InputAdornment,
  FormControlLabel,
  DialogActions,
  DialogContent,
  useMediaQuery
} from '@material-ui/core';
import { useTheme, alpha } from '@material-ui/core/styles';
import { LoadingButton } from '@material-ui/lab';
import {
  Add as AddIcon,
  Check,
  Close,
  Edit as EditIcon,
  LocalOffer,
  HomeOutlined,
  SellOutlined,
  VpnKeyOutlined,
  PaidOutlined,
  SupportAgentOutlined,
  GavelOutlined,
  SquareFootOutlined
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';

import { parsePhoneNumber } from 'utils/Haim/phoneNumbersUtils';
import useContactHandler from 'hooks/crm/useContactHandler';
import ContactPhoneDuplicateAlert from './ContactPhoneDuplicateAlert';
import { isValidEmail, isValidIsraeliId, isStrictValidPhone } from 'utils/fieldValidators';
import { buildContactOriginOptions, CUSTOM_CONTACT_ORIGIN_VALUE, MANUAL_CONTACT_ORIGIN } from 'utils/contacts/contactOrigin';
import { formatBudgetShort, formatBudgetRangeShortRtl } from 'utils/contacts/budgetFormat';
import { mappedCustomerTypes } from 'utils/contacts/mappedTs';
import { fetchListingsByIds } from 'utils/listings/fetchListings';
import { CustomNumericFormat } from 'components/_dashboard/listings/modal/core/CustomNumericFormat';
import { PropertyTypeSelect } from 'components/__haim/Common/PropertyTypeSelect';
import { ListingBooleanPropertiesDetailsBag } from 'components/_dashboard/listingsMatch/ListingPropertiesAndAdditionalDetails';
import { LocationPicker } from 'components/_dashboard/listingsMatch/LocationPicker';
import { NewListingPicker } from 'components/_dashboard/signatures/NewListingPicker';
import { NewListingDialog } from 'components/_dashboard/listings/modal/dialog/NewListingDialog';
import { isVirtualPhone } from 'components/_dashboard/listings/seller/sellerPhone';
import { ContactDialogInputPhoneNumber } from './ContactDialogInputPhoneNumber';
import { ChangePhoneDialog } from './ChangePhoneDialog';
import { ContactDialogInputTagsAutocomplete } from './ContactDialogInputTagsAutocomplete';
import { ContactCreatedCelebration } from './ContactCreatedCelebration';
import { ContactTableAiFieldFlag } from '../table/rows/ContactTableAiFieldFlag';
import { CrmStatusIcon, CrmHotFireIcon } from '../crm/CrmIcons';
import { useLegacyContactWrites } from 'hooks/contacts/useLegacyContactsBook';
import useAuth from 'hooks/useAuth';
import useContactWrites from 'hooks/contacts/useContactWrites';
import useContactSmartDefaults from 'hooks/useContactSmartDefaults';
import { db } from 'firebaseConfig';
import { doc } from 'firebase/firestore';
import useContactByPhone from 'hooks/contacts/useContactByPhone';
import useDebounce from 'hooks/useDebounce';
import {
  CONTACT_NOT_IN_YOUR_BOOK_MESSAGE,
  describeContactValidationFailure,
  isContactBookBusyError,
  isContactNotInYourBookError,
  isContactDuplicateError
} from 'utils/contacts/contactWriteErrors';
import {
  rooms as roomsScale,
  crmStatuses,
  crmStatusLabels,
  getContactCrmSides,
  getContactOriginLabel,
  getContactCustomerTypes,
  buildCustomerTypesUpdate,
  getCrmSubStatusDefinitionsForSides,
  MAX_INTERESTED_IN_LOCATIONS,
  propertyTypes as ALL_PROPERTY_TYPES
} from '../../../../@types/sharedSchema';
import type { ChangeEvent, FormEvent, ReactNode } from 'react';
import type { DialogProps } from '@material-ui/core';
import type { EditableContact } from 'utils/contacts/new';
import type {
  REST_schema,
  CrmStatus,
  CustomerType,
  CrmPipelineSide,
  TransactionType,
  RealEstateContact,
  ContactBudgetRange,
  ProcessedListingData,
  GenericPropertyDetails,
  ProcessedListingAddress
} from '../../../../@types/sharedSchema';

// ──────────────────────────────────────────────────────────────────────────────
// The new/edit contact experience — rebuilt from scratch after the approved
// mock (docs/crm/mocks in webot-server): live avatar + name header, completeness
// meter, tactile customer-type cards, a prominent status+hot picker, budget as
// an RTL range slider with editable prices + smart presets, rooms range pills,
// suggested areas, editable lead source, and a celebration on create.
// Single component for create / edit / bulk-edit — every caller shares it.
// ──────────────────────────────────────────────────────────────────────────────

const MemoLocationPicker = memo(LocationPicker);
const MemoTagsAutocomplete = memo(ContactDialogInputTagsAutocomplete);

// Customer-type dropdown options: the 4 main market roles first (each with its
// icon + accent), the professional roles grouped at the end under "אחרים".
type TypeOptionConfig = { type: CustomerType; Icon: typeof HomeOutlined; accent: string };
const MAIN_TYPE_OPTIONS: TypeOptionConfig[] = [
  { type: 'קונה', Icon: HomeOutlined, accent: '#3366FF' },
  { type: 'מוכר', Icon: SellOutlined, accent: '#00AB55' },
  { type: 'שוכר', Icon: VpnKeyOutlined, accent: '#7635dc' },
  { type: 'משכיר', Icon: PaidOutlined, accent: '#E8794A' }
];
const OTHER_TYPE_OPTIONS: TypeOptionConfig[] = [
  { type: 'מתווך', Icon: SupportAgentOutlined, accent: '#637381' },
  { type: 'עו"ד', Icon: GavelOutlined, accent: '#637381' },
  { type: 'שמאי', Icon: SquareFootOutlined, accent: '#637381' }
];
const TYPE_OPTION_BY_TYPE = new Map<CustomerType, TypeOptionConfig>(
  [...MAIN_TYPE_OPTIONS, ...OTHER_TYPE_OPTIONS].map((option) => [option.type, option])
);

// Fallback popular property types when the realtor has no stable history yet.
// Must match the exact strings of the shared `propertyTypes` enum.
const STATIC_POPULAR_PROPERTY_TYPES = ['דירה', 'דירת גן', 'גג/ פנטהאוז', 'דופלקס', "בית פרטי/ קוטג'", 'יחידת דיור'];

// Legacy / malformed contact docs can hold a non-array value in fields we expect
// to be arrays (tags, interestedInLocations, interestedInPropertyTypes). A bare
// `?? []` only guards null/undefined, so a stored string/object would slip through
// and blow up on `.filter`/`.map`. Coerce anything non-array to an empty array.
function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

// Title hierarchy — ONE consistent treatment across the whole form (module-scope
// so the top-level sections and the sub-components below cannot drift apart):
// section titles are 14/800 text.primary, field/block labels are 13/700
// text.primary. Never mix in a grey/uppercase variant — the professional pass
// (2026-08) unified these after mixed grey+black titles read as misalignment.
const sectionLabelSx = { fontSize: 14, fontWeight: 800, color: 'text.primary' } as const;
// Sub-headings INSIDE a section (budget / rooms / property types / …).
const blockLabelSx = { fontSize: 13, fontWeight: 700, color: 'text.primary', mb: 1 } as const;

// Fields the FORM ITSELF derives, and which must therefore never reach the wire.
//
// The edit diff drops every field the user did not touch, which is what keeps the ~18
// server-rejected keys carried by a hydrated document out of the payload. These are the
// exception the diff cannot catch: they change whenever their canonical sibling does, so
// they look edited. `customerType` is the live case — `handleCustomerTypesChange` writes
// it alongside `customerTypes` (via `buildCustomerTypesUpdate`) so that this form's own
// reads stay coherent — and it is exactly one of the two legacy mirrors the server
// refuses, because `updateContactOfUser` derives both from the real document. Left in,
// picking two customer types would 400 the entire save.
//
// The rest are the same class — Cloud-Function-stamped from fields in this payload — and
// are named here so that a future handler that starts maintaining one locally cannot
// silently reintroduce the same failure.
const DERIVED_FIELDS_NEVER_SENT = new Set([
  'customerType',
  'customerSeriousness',
  'contactFullName',
  'interestedInCitiesDistinct',
  'interestedInNeighborhoodsDistinct',
  // Stamped by the server's own pipelines (call analysis, bulk messaging), and REFUSED by the
  // contact endpoints from any client (`contactWriteRules.ts`). `AICustomerDetailsMetadata`
  // reaches the form through the call row's seed (`buildCallContactSeed`), which keeps it for
  // the dialog's per-field AI markers; sending it made every create from a call with an AI
  // analysis fail with "השמירה נכשלה בגלל תקלה במערכת" (2026-10-14, Idan Kornfeld).
  'AICustomerDetailsMetadata',
  'isInSpamDatabase',
  'bulkMessagingNumberDoesntExists',
  'lastBulkMessageReceived'
]);

const SALE_STEP = 50_000;
const RENT_STEP = 250;
const DEFAULT_SALE_RANGE: ContactBudgetRange = { min: 1_500_000, max: 2_500_000 };
const DEFAULT_RENT_RANGE: ContactBudgetRange = { min: 4_500, max: 6_500 };

/**
 * Which budget control a stored contact opens in — and the one place that decides
 * whether a stored lower bound EXISTS.
 *
 * ⚠️ A FLOOR OF ZERO IS A FLOOR. This was three separate `customerBudgetMin ? …`
 * truthiness tests, so a contact saved as 0–1.5M reopened in 'exact' mode showing
 * ₪1.5M, and the next save — which writes `customerBudgetMin: null` whenever the
 * mode is 'exact' — destroyed the floor for real. The realtor saw a number they had
 * not typed and a range they had entered silently become an exact budget.
 *
 * 0 is meaningfully DIFFERENT from absent here, which is why it has to survive: a
 * lone `customerBudget` is treated everywhere as an approximate target (the matches
 * tab seeds ±10% around it; listing alerts derive an asymmetric floor below it and
 * allow listings above it), whereas 0–1.5M is a hard ceiling with no floor.
 *
 * Kept to the same `typeof === 'number'` test the form's own `saleBudgetMin` /
 * `rentBudgetMin` readers use, so the mode and the value can never disagree — a
 * string-typed legacy value reads as absent on both sides rather than opening a
 * range control with nothing in it. Deliberately NOT crmCellEditModel's
 * `isSetNumber`: that one answers "is there a budget at all", where 0 means no.
 */
const budgetModeFor = (storedMin: unknown): 'range' | 'exact' =>
  typeof storedMin === 'number' && isFinite(storedMin) ? 'range' : 'exact';

// Stable default so hosts that pass no catalog still get every property type
// (module-level const keeps referential identity across renders for the memos).
const DEFAULT_PROPERTY_TYPES: string[] = [...ALL_PROPERTY_TYPES];

type EditContactDialogProps = Pick<DialogProps, 'open'> & {
  contact?: Partial<RealEstateContact>;
  selected?: string[];
  customTitle?: string;
  /**
   * Property-type catalog for the seeker profile chips. Optional — hosts that
   * don't pass one (e.g. CalendarCustomerCard rows inside the calendar event
   * editor) get the full static taxonomy, so the dropdown can never render
   * empty.
   */
  propertyTypes?: string[];
  forceCreation?: boolean;
  customOkButtonText?: string;
  customCancelButtonText?: string;
  onClose: () => void;
  // On create, the newly-created contact is passed back so callers can
  // auto-select it (e.g. the signature send form). Optional arg — existing
  // callers that ignore it stay source-compatible.
  onSuccess?: (created?: Partial<RealEstateContact>) => void;
  /**
   * EDIT MODE ONLY — fired after a successful PHONE-NUMBER change (the "עדכון מספר"
   * flow beside the locked phone field), AFTER this dialog has closed itself. The
   * change is a server-side re-key: `result.newContactId` is the normalized new id and
   * the old document no longer exists — which is why the dialog closes rather than
   * keep a form keyed to a deleted doc. The in-memory `allContacts` array, the
   * `?contact=` URL param and the customer-page session caches are already repaired
   * by the flow itself (see ChangePhoneDialog); hosts only need this to fix state
   * THEY own (e.g. the CRM row list — see `CrmEditContactDialog.onRekeyed`).
   */
  onPhoneChanged?: (oldContactId: string, result: REST_schema.ChangeContactPhoneResponse) => void;
  /**
   * EDIT MODE ONLY — whether the "עדכון מספר" (phone re-key) affordance is offered
   * beside the locked phone field. Default TRUE: the legacy /dashboard/contacts host is
   * always the viewer's own book. The CRM passes `false` for a colleague-owned row
   * (`CrmEditContactDialog`), because `change-phone` is own-book-only on the server —
   * the button would render and then 404 on every attempt.
   */
  allowPhoneChange?: boolean;
  /**
   * CREATE MODE ONLY — the phone this dialog was SEEDED with is a Yad2 virtual
   * number (`isVirtualPhone`), so a caveat renders under the phone field.
   *
   * A contact's document id IS its phone, so saving a mask mints a contact
   * nobody can ever call and that only the re-key flow can repair. This field is
   * the only place in the app where that is still correctable for free — which
   * is why the caveat belongs here rather than on the surface the realtor came
   * from, where it could only be read, not acted on.
   *
   * A FLAG FROM THE CALLER, not `isVirtualPhone(the field)`: 055 is also a
   * perfectly ordinary mobile prefix, and warning about every 055 a realtor types
   * by hand would train her to ignore the one case that matters. The caller knows
   * the number came off a scraped ad; the dialog only re-checks that the value
   * she is about to save is STILL masked, so the caveat clears as she fixes it.
   */
  seededPhoneIsMasked?: boolean;
  /** Optional stacking override, for callers opening this from on top of a
   *  non-MUI overlay (e.g. the signatures detail side-panel at zIndex 2001,
   *  which a default 1300 modal would open underneath). */
  zIndex?: number;

  // ── SERVER-SIDE BULK EDIT (the CRM page) ───────────────────────────────────────
  //
  // `selected` puts this dialog in bulk mode and saves through `batchUpdate`, i.e. a
  // client-side Firestore batch into `dashboard-users-v2/{signed-in email}/contacts`.
  // That is right for /dashboard/contacts, where every row belongs to the reader. It is
  // unusable on the CRM page for two INDEPENDENT reasons:
  //
  //  1. An agency row belongs to another agent, and `set(..., { merge: true })` does not
  //     fail on a document you do not own — it MINTS one under your own email. A bulk
  //     edit of a teammate's contacts would silently fork the agency's book.
  //  2. A CRM selection is frequently a FILTER ("all 8,102 matching"), and a `string[]`
  //     cannot express one. Enumerating it would mean paging the whole book first, and
  //     acting on a set that had already drifted while it paged.
  //
  // So the CRM page passes `onBulkSubmit` INSTEAD of `selected`: the same form, the same
  // "only SET what was filled" semantics, but the save is handed back to the caller,
  // which posts it to `POST /api/v1/contacts/bulk-update` — where owner scoping, the
  // agency's `crmPermissions`, the `maxBulkEdit` cap, the drift check and the
  // `crm_contact_audit` row all live.
  //
  // EVERY PROP BELOW IS OPTIONAL AND ABSENT ON THE LEGACY PATH, so /dashboard/contacts
  // keeps the behaviour it has today, down to the byte.
  /**
   * Receives the patch that will be written. RESOLVING closes the dialog; REJECTING
   * leaves it open, because a bulk refusal is the caller's to explain (see
   * `renderBulkFooter`).
   */
  onBulkSubmit?: (fields: Record<string, unknown>) => Promise<void>;
  /** Audience size for the header. Only read when there is no `selected` array. */
  bulkCount?: number;
  /** Rendered FIRST in the body — guidance to read before filling the form. */
  bulkIntro?: ReactNode;
  /** Rendered LAST in the body — bulk-only controls this form hides (see `!bulkEdit`). */
  bulkExtraFields?: ReactNode;
  /**
   * The values behind `bulkExtraFields`, merged into the patch through the SAME strip.
   *
   * A data prop rather than "the caller merges them in its own submit handler", because
   * the patch is what the review renders and what the empty-patch check reads: a field
   * merged downstream of both would be invisible in the review and would leave the save
   * button disabled while the realtor was looking at a value they had just filled in.
   * One patch, one place, three consumers.
   */
  bulkExtraPatch?: Record<string, unknown>;
  /**
   * Rendered directly above the actions, receiving the patch that will ACTUALLY be sent
   * (derived fields removed, empty values dropped).
   *
   * A render prop rather than a lifted `fields` + a plain node, so that a form keystroke
   * can never become a parent state update — and so the thing reviewed is provably the
   * thing sent, rather than a copy that can drift from it.
   *
   * `resetField` puts ONE key back to untouched, so a review can be a CONTROL rather than
   * a warning. It is needed because several of this form's controls arm a write on a
   * single click and offer no way back: the budget block's "טווח" toggle writes the
   * SUGGESTED range (₪1.5M–2.5M) the moment it is pressed, the 🔥 toggle tapped twice
   * writes `false`, and "ללא סטטוס" writes `null`. On one contact none of that matters —
   * the realtor retypes the old value. On two thousand there is no old value to retype,
   * so without this the only way to un-arm a field is to close the dialog and start again.
   */
  renderBulkFooter?: (fields: Record<string, unknown>, resetField: (key: string) => void) => ReactNode;
  /** Blocks the save for a reason the CALLER owns — the agency's cap, a drift refusal. */
  bulkSaveDisabled?: boolean;
};

/**
 * "IS THIS NUMBER ALREADY ONE OF MY CONTACTS?" — the form's dedupe, through the one reader of
 * a contact by phone (`useContactByPhone`): the server's document in the new way, every
 * spelling of the number, live. It used to be a one-time read of
 * `dashboard-users-v2/{me}/contacts/{phone}`, which in a shared office is the member's frozen
 * copy from before the merge, or nothing — and "edit instead" would then have saved that
 * copy's old values over the office's record.
 *
 * The phone field is typed into character by character and `parsePhoneNumber` returns a value
 * well before the user is done, so the check waits for the typing to settle, and an answer
 * counts only for the phone on screen now. A check that fails or is still out is no
 * duplicate: it must never block a legitimate create.
 *
 * A hook of its own rather than three lines in the component: `EditContactDialog` is large
 * enough that every branch added to its body tips `react-hooks/rules-of-hooks` into reporting
 * each later hook as conditional (its path count loses precision past 2^53).
 */
function useExistingContactForPhone(phone: string | null): RealEstateContact | undefined {
  const settledPhone = useDebounce(phone, 400);
  const check = useContactByPhone(settledPhone);
  if (!phone || settledPhone !== phone) return undefined;
  return check.contact;
}

export function EditContactDialog({
  open,
  contact,
  selected,
  customTitle,
  propertyTypes = DEFAULT_PROPERTY_TYPES,
  forceCreation,
  customOkButtonText,
  customCancelButtonText,
  onClose,
  onSuccess,
  onPhoneChanged,
  allowPhoneChange = true,
  seededPhoneIsMasked,
  zIndex,
  onBulkSubmit,
  bulkCount,
  bulkIntro,
  bulkExtraFields,
  bulkExtraPatch,
  renderBulkFooter,
  bulkSaveDisabled
}: EditContactDialogProps): JSX.Element {
  const { t: translate } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    user: { email }
  } = useAuth();
  // The browser-direct `batchUpdate` survives for ONE caller: the old contacts page's
  // toolbar, which opens this dialog in its legacy bulk mode (`selected`). The CRM page's
  // bulk edit never reaches it (see `onBulkSubmit`).
  const { batchUpdate } = useLegacyContactWrites();
  // Create and edit both go through the write chokepoint, not the API client: it is what
  // tells the local stores (the legacy book, the lookup store) that the write happened, so
  // every screen showing this contact repaints without a reload.
  const { createContact, updateContact } = useContactWrites();
  const { defaults: smartDefaults } = useContactSmartDefaults();

  const [contactForm, setContactForm] = useState<Partial<EditableContact>>(contact ?? {});
  const [tags, setTags] = useState<string[]>(asArray<string>(contact?.tags));
  const [interestedInLocations, setInterestedInLocations] = useState<ProcessedListingAddress[]>(
    asArray<ProcessedListingAddress>(contact?.interestedInLocations)
  );
  // "Could not place this" texts written by call transcription (see
  // `RealEstateContact.unresolvedLocations`). They ride on `contactForm` like any plain field,
  // so the edit diff sends them only when changed and `[]` clears them server-side. Each chip
  // seeds the area picker with its text; a pick made while that chip is "being fixed" removes
  // the chip, and ✕ removes it without picking.
  const unresolvedLocations = asArray<string>(contactForm.unresolvedLocations);
  const [areaSearchSeed, setAreaSearchSeed] = useState<{ text: string; nonce: number } | null>(null);
  const fixingUnresolvedRef = useRef<string | null>(null);
  const interestedInLocationsCountRef = useRef(0);
  useEffect(() => {
    interestedInLocationsCountRef.current = interestedInLocations.length;
  }, [interestedInLocations.length]);

  const removeUnresolvedLocation = useCallback((text: string): void => {
    setContactForm((prev) => ({
      ...prev,
      unresolvedLocations: asArray<string>(prev.unresolvedLocations).filter((entry) => entry !== text)
    }));
  }, []);

  const startFixingUnresolvedLocation = useCallback((text: string): void => {
    fixingUnresolvedRef.current = text;
    // The picker's "name + city" search does not read the comma the server writes between
    // the part and its city; a space is what a realtor would have typed.
    setAreaSearchSeed({ text: text.replace(/\s*,\s*/g, ' '), nonce: Date.now() });
  }, []);

  const handleInterestedInLocationsChange = useCallback(
    (locations: ProcessedListingAddress[]): void => {
      const picked = locations.length > interestedInLocationsCountRef.current;
      setInterestedInLocations(locations);
      if (picked && fixingUnresolvedRef.current) {
        removeUnresolvedLocation(fixingUnresolvedRef.current);
        fixingUnresolvedRef.current = null;
      }
    },
    [removeUnresolvedLocation]
  );
  const [interestedInPropertyTypes, setInterestedInPropertyTypes] = useState<string[]>(
    asArray<string>(contact?.interestedInPropertyTypes)
  );
  const [sellingListings, setSellingListings] = useState<ProcessedListingData[]>([]);
  const [rentingOutListings, setRentingOutListings] = useState<ProcessedListingData[]>([]);
  const [loadingContactListings, setLoadingContactListings] = useState(false);
  const [listingDialogFor, setListingDialogFor] = useState<TransactionType | null>(null);
  const [loading, setLoading] = useState(false);
  // Surfaced above the save button. Cleared on every submit and on close.
  const [saveError, setSaveError] = useState<string | null>(null);
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);
  const [editingExisting, setEditingExisting] = useState(false);
  const [celebrateContact, setCelebrateContact] = useState<Partial<RealEstateContact> | null>(null);
  // "Custom source" mini-input under the lead-source dropdown (opened via the
  // dedicated menu entry — the dropdown itself stays managed values only).
  const [customSourceOpen, setCustomSourceOpen] = useState(false);
  const [customSourceDraft, setCustomSourceDraft] = useState('');
  // Customer-type dropdown: controlled open so it can close after every pick
  // (single-pick feel) while still allowing reopening to pick more.
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  // Dirty-close guard: the small "discard changes?" confirmation.
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  // The phone-number-change (re-key) dialog — edit mode only, see the affordance
  // beside the locked phone field.
  const [changePhoneOpen, setChangePhoneOpen] = useState(false);
  // Budget mode per market. 'range' shows a dual-thumb slider + two prices.
  const [saleBudgetMode, setSaleBudgetMode] = useState<'range' | 'exact'>(budgetModeFor(contact?.customerBudgetMin));
  const [rentBudgetMode, setRentBudgetMode] = useState<'range' | 'exact'>(budgetModeFor(contact?.customerBudgetRentMin));
  const [editingPrice, setEditingPrice] = useState<string | null>(null); // e.g. 'customerBudgetMin'

  // Two bulk flavours, one form. `serverBulk` is the CRM page's: no `selected` array,
  // because its selection can be a filter the browser never enumerates.
  const serverBulk = !!onBulkSubmit;
  const bulkEdit = !!selected || serverBulk;
  /** The avatar badge and the "applies to N contacts" subtitle. */
  const bulkAudienceCount = selected?.length ?? bulkCount ?? 0;
  const editMode = editingExisting ? true : forceCreation ? false : bulkEdit || Boolean(contact);
  const showCelebrationOnCreate = !onSuccess;

  // ── hydrate on open (edit mode) / reset on close ──
  const hydrateContactListings = useCallback(async (c?: Partial<RealEstateContact> | null): Promise<void> => {
    const sellingIds = c?.sellingListingIds ?? [];
    const rentingOutIds = c?.rentingOutListingIds ?? [];
    if (!sellingIds.length && !rentingOutIds.length) {
      setSellingListings([]);
      setRentingOutListings([]);
      return;
    }
    setLoadingContactListings(true);
    try {
      const [selling, rentingOut] = await Promise.all([
        sellingIds.length ? fetchListingsByIds(sellingIds) : [],
        rentingOutIds.length ? fetchListingsByIds(rentingOutIds) : []
      ]);
      setSellingListings(selling);
      setRentingOutListings(rentingOut);
    } catch (err) {
      console.error("Failed loading the contact's listings", err);
    } finally {
      setLoadingContactListings(false);
    }
  }, []);

  // ── dirty-close guard baseline ──
  // Baseline = the contact the form was hydrated from (switches when the user
  // jumps to "edit existing" on a duplicate phone). Dirtiness is a structural
  // compare of the form state against that baseline, so closing an untouched
  // dialog never nags while any real edit gets a confirmation before discard.
  // Set by the hydration effect below — NEVER re-synced from the live prop on
  // its own, or an external write mid-edit would shift the baseline under an
  // untouched form and make closing it nag "discard changes?".
  const baselineContactRef = useRef<Partial<RealEstateContact> | null>(contact ?? null);

  // Hydrate ONCE per (open, contact id). Hosts on the customer page pass a
  // LIVE contact (useLiveContact snapshot), so an external write mid-edit — the
  // flattenInterestedInLocations CF stamping derived fields seconds after a
  // save, the AI call pipeline, another tab — produces a NEW contact object
  // with the SAME id while the dialog is open. Re-running the hydration then
  // would silently wipe the user's in-progress edits. A different id (a
  // late-arriving contact on hosts that open the dialog before their fetch
  // resolves) still hydrates normally.
  const hydratedContactKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading || !open || !contact) return;
    const contactKey = contact.id ?? '__no_id__';
    if (hydratedContactKeyRef.current === contactKey) return;
    hydratedContactKeyRef.current = contactKey;
    baselineContactRef.current = contact;
    setContactForm(contact);
    setTags(asArray<string>(contact.tags));
    setInterestedInLocations(asArray<ProcessedListingAddress>(contact.interestedInLocations));
    setInterestedInPropertyTypes(asArray<string>(contact.interestedInPropertyTypes));
    setSaleBudgetMode(budgetModeFor(contact.customerBudgetMin));
    setRentBudgetMode(budgetModeFor(contact.customerBudgetRentMin));
    void hydrateContactListings(contact);
  }, [open, contact, loading, hydrateContactListings]);

  // Wipe ALL transient form state whenever the dialog closes, so the next open
  // starts from a clean slate (the [open, contact] effect above re-populates
  // edit mode on the next open).
  useEffect(() => {
    if (open) return;
    hydratedContactKeyRef.current = null;
    setEditingExisting(false);
    setContactForm(contact ?? {});
    setTags(asArray<string>(contact?.tags));
    setInterestedInLocations(asArray<ProcessedListingAddress>(contact?.interestedInLocations));
    setInterestedInPropertyTypes(asArray<string>(contact?.interestedInPropertyTypes));
    setSellingListings([]);
    setRentingOutListings([]);
    setListingDialogFor(null);
    setMoreDetailsOpen(false);
    setCustomSourceOpen(false);
    setCustomSourceDraft('');
    setTypeMenuOpen(false);
    setConfirmDiscardOpen(false);
    setEditingPrice(null);
    setCelebrateContact(null);
    setSaveError(null);
    setChangePhoneOpen(false);
  }, [open, contact]);

  // ── field handlers (stable identities so memoized children skip re-renders) ──
  const handleChange = useCallback(({ target: { name, value } }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setContactForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setFormValue = useCallback((name: string, value: unknown): void => {
    setContactForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChangePropertyDetails = useCallback(
    (name: keyof GenericPropertyDetails) => (): void => {
      setContactForm((prev) => ({ ...prev, [name]: !prev[name] }));
    },
    []
  );

  // ── CRM status + hot (legacy customerSeriousness mirror derives at the write chokepoint) ──
  const handleChangeCrmStatus = useCallback((status: CrmStatus | null, subStatus: string | null): void => {
    setContactForm((prev) => ({ ...prev, crmStatus: status, crmSubStatus: subStatus }));
  }, []);

  const handleToggleCrmHot = useCallback((): void => {
    setContactForm((prev) => ({ ...prev, crmIsHot: !prev.crmIsHot }));
  }, []);

  // ── additional phone numbers: array editor (add / edit / remove rows) ──
  const addAdditionalPhone = useCallback(() => {
    setContactForm((prev) => ({ ...prev, additionalPhoneNumbers: [...(prev.additionalPhoneNumbers ?? []), ''] }));
  }, []);
  const updateAdditionalPhone = useCallback((index: number, value: string) => {
    setContactForm((prev) => ({
      ...prev,
      additionalPhoneNumbers: (prev.additionalPhoneNumbers ?? []).map((p, i) => (i === index ? value : p))
    }));
  }, []);
  const removeAdditionalPhone = useCallback((index: number) => {
    setContactForm((prev) => ({
      ...prev,
      additionalPhoneNumbers: (prev.additionalPhoneNumbers ?? []).filter((_, i) => i !== index)
    }));
  }, []);

  // ── name / avatar header ──
  const firstName = (contactForm.callerFirstName ?? '').trim();
  const lastName = (contactForm.callerLastName ?? '').trim();
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim() || firstName.slice(0, 2);
  const [avatarBump, setAvatarBump] = useState(false);
  const prevInitialsRef = useRef(initials);
  useEffect(() => {
    if (initials && initials !== prevInitialsRef.current) {
      setAvatarBump(true);
      const timer = setTimeout(() => setAvatarBump(false), 320);
      prevInitialsRef.current = initials;
      return () => clearTimeout(timer);
    }
    prevInitialsRef.current = initials;
    return undefined;
  }, [initials]);

  // ── phone + dedupe ──
  //
  // "Is this number already one of my contacts?" is answered by READING THE ONE DOCUMENT.
  // The contact id IS the international phone (measured: 47,726 of 47,726 ids are
  // phone-shaped), so a single `get()` answers it, on every page.
  //
  // It used to ask the in-browser `useLegacyContactsBook().allContacts` array first and fall back to this
  // read only when the array was not loaded. The array is the caller's whole book, held
  // only while one of the two legacy tables has downloaded it — so the same dialog gave an
  // instant answer on one page and a document read on every other, and its guard rested on
  // a download most sessions no longer make. One path, the same everywhere.
  //
  // This is a courtesy, not the guard: it lets the dialog offer "edit that contact
  // instead" before the realtor has filled the form in. What actually refuses a duplicate
  // is the server's 409 on create (see `isContactDuplicateError`).
  // ── THE PHONE COMES FROM THE DOCUMENT ID, NOT FROM `callerPhoneNumber` ────────────
  //
  // In CREATE mode the number is what the realtor is typing and there is no document yet,
  // so the form field is the only place it can live. In EDIT mode the identity is the doc
  // id and the stored `callerPhoneNumber` is ignored outright: it is absent on ~90% of
  // contact documents (measured 2026-09-01: present on 267 of 2,683 across 60 accounts)
  // and, where present, disagrees with the id often enough to matter — local-format
  // `0503045800`, or `+33 6 16 79 08 94` with spaces and RTL marks.
  //
  // This used to work anyway, by accident: `postProcessContact` overwrites the field with
  // the id on read, so every host that normalised its contact handed us a correct value.
  // The contact page does not (`ContactDetailView` renders the raw live snapshot), which
  // is where the empty phone box came from.
  //
  // DISPLAY ONLY — deliberately not written back into `contactForm`. The form IS the save
  // payload; `POST /api/v1/contacts/update` refuses `callerPhoneNumber` by design (writing
  // it "creates a contact whose stored number disagrees with the one it is filed under"),
  // and `buildEditDiff` would start sending it the moment it differed from the baseline.
  const callerPhoneNumber = editMode ? (contactForm.id as string | undefined) : contactForm.callerPhoneNumber;
  const phoneNumber = useMemo(() => (callerPhoneNumber ? parsePhoneNumber(callerPhoneNumber) : null), [callerPhoneNumber]);
  // The seeded-mask caveat — see `seededPhoneIsMasked`. Both halves matter: the
  // caller says the number came off a scraped ad, and the LIVE field value says
  // whether it is still a mask, so correcting it clears the notice as she types.
  const showMaskedPhoneCaveat = !!seededPhoneIsMasked && !editMode && !bulkEdit && isVirtualPhone(callerPhoneNumber ?? '');
  // `E164Number` is a branded string; every other consumer in this file widens it the
  // same way (see the create path below).
  const dedupePhone: string | null =
    !editMode && !bulkEdit && open ? ((phoneNumber?.number as string | undefined) ?? null) : null;

  const existingContactForPhone = useExistingContactForPhone(dedupePhone);
  const isDuplicatePhone = !!existingContactForPhone;

  /**
   * ── AND THE SECOND QUESTION THE DEDUPE ABOVE CANNOT ASK ────────────────────────
   *
   * Everything above answers "do I already have this person": it reads the caller's own
   * book (`useContactByPhone`). It cannot see a COLLEAGUE.
   *
   * That second question is the one that changes behaviour. A duplicate costs a wasted
   * card; calling a lead a colleague is already running costs the agency the lead, and it
   * is the failure the whole agency view was built to prevent.
   *
   * Same phone key, and the same never-block-a-create rule: a pending or failed lookup is
   * `undefined` and renders nothing. It issues no request at all for the ~95% of the fleet
   * in no agency, and on a warm CRM page it issues none either — the module store already
   * holds the answer the list fetched.
   */
  const { handler: phoneHandler } = useContactHandler(dedupePhone);
  // Never tell a user about themselves: that is the fastest way to make a warning
  // ignorable, and "you are handling this contact" is not news to the person handling it.
  const collidingHandler = phoneHandler && !phoneHandler.isMe && !phoneHandler.stale ? phoneHandler : null;

  const handleEditExisting = (): void => {
    if (!existingContactForPhone) return;
    baselineContactRef.current = existingContactForPhone;
    setContactForm(existingContactForPhone);
    setTags(asArray<string>(existingContactForPhone.tags));
    setInterestedInLocations(asArray<ProcessedListingAddress>(existingContactForPhone.interestedInLocations));
    setInterestedInPropertyTypes(asArray<string>(existingContactForPhone.interestedInPropertyTypes));
    setSaleBudgetMode(budgetModeFor(existingContactForPhone.customerBudgetMin));
    setRentBudgetMode(budgetModeFor(existingContactForPhone.customerBudgetRentMin));
    void hydrateContactListings(existingContactForPhone);
    setEditingExisting(true);
  };

  // ── customer types ──
  const selectedCustomerTypes = useMemo(
    () => getContactCustomerTypes({ customerType: contactForm.customerType, customerTypes: contactForm.customerTypes }),
    [contactForm.customerType, contactForm.customerTypes]
  );
  // The dropdown closes after EVERY pick (single-pick feel); reopening it to
  // pick more types is the multi-select path.
  const handleCustomerTypesChange = useCallback((next: CustomerType[]): void => {
    setContactForm((prev) => ({ ...prev, ...buildCustomerTypesUpdate(next) }));
    setTypeMenuOpen(false);
  }, []);
  const getTypeLabel = useCallback(
    (type: CustomerType): string => translate(`contactsPage.customerType.${mappedCustomerTypes[type]}`) as string,
    [translate]
  );
  const crmSides = useMemo(() => getContactCrmSides({ customerTypes: selectedCustomerTypes }), [selectedCustomerTypes]);

  const isBuyer = selectedCustomerTypes.includes('קונה');
  const isTenant = selectedCustomerTypes.includes('שוכר');
  const isSeller = selectedCustomerTypes.includes('מוכר');
  const isLandlord = selectedCustomerTypes.includes('משכיר');
  // Bulk edit: the selection may mix types, so the seeker fields stay active (old-form
  // behavior); the per-contact listings sections can't be bulk-assigned and stay hidden.
  const seekerActive = bulkEdit || isBuyer || isTenant;
  const ownerActive = !bulkEdit && (isSeller || isLandlord);
  const showSaleBudget = bulkEdit || isBuyer;
  const showRentBudget = bulkEdit || isTenant;

  const seekerTitle = bulkEdit || (isBuyer && isTenant) ? 'פרופיל קונה / שוכר' : isTenant ? 'פרופיל שוכר' : 'פרופיל קונה';
  const seekerEmoji = isTenant && !isBuyer && !bulkEdit ? '🔑' : '🏡';
  const seekerAccent = isTenant && !isBuyer && !bulkEdit ? '#7635dc' : '#3366FF';
  const ownerTitle = isSeller && isLandlord ? 'פרופיל מוכר ומשכיר' : isLandlord ? 'פרופיל משכיר' : 'פרופיל מוכר';
  const ownerEmoji = isLandlord && !isSeller ? '💰' : '🏷️';

  // ── min/max range fields (rooms / floors / built area / plot size): the pair
  //    always satisfies min <= max by PUSHING the other bound — raising min
  //    above max drags max up with it; lowering max below min drags min down. ──
  const setRangeBound = useCallback((minField: string, maxField: string, bound: 'min' | 'max', v: number | null): void => {
    setContactForm((prev) => {
      const record = prev as Record<string, unknown>;
      const other = (bound === 'min' ? record[maxField] : record[minField]) as number | null | undefined;
      const patch: Record<string, unknown> = { [bound === 'min' ? minField : maxField]: v };
      if (v != null && other != null) {
        if (bound === 'min' && v > other) patch[maxField] = v;
        if (bound === 'max' && v < other) patch[minField] = v;
      }
      return { ...prev, ...patch } as Partial<EditableContact>;
    });
  }, []);
  // Free-typed numeric ranges (built area / plot size) don't push while typing —
  // that would drag the other bound mid-keystroke. Instead they normalize on
  // blur: an inverted pair is swapped.
  const normalizeRange = useCallback((minField: string, maxField: string): void => {
    setContactForm((prev) => {
      const record = prev as Record<string, unknown>;
      const min = record[minField] as number | null | undefined;
      const max = record[maxField] as number | null | undefined;
      if (min != null && max != null && min > max) return { ...prev, [minField]: max, [maxField]: min } as Partial<EditableContact>;
      return prev;
    });
  }, []);

  // ── budget ──
  const saleBudget = typeof contactForm.customerBudget === 'number' ? contactForm.customerBudget : null;
  const saleBudgetMin = typeof contactForm.customerBudgetMin === 'number' ? contactForm.customerBudgetMin : null;
  const rentBudget = typeof contactForm.customerBudgetRent === 'number' ? contactForm.customerBudgetRent : null;
  const rentBudgetMin = typeof contactForm.customerBudgetRentMin === 'number' ? contactForm.customerBudgetRentMin : null;

  const applyBudget = useCallback((market: 'sale' | 'rent', mode: 'range' | 'exact', min: number | null, max: number | null): void => {
    if (market === 'sale') {
      setContactForm(
        (prev) =>
          ({ ...prev, customerBudget: (max ?? undefined) as number | undefined, customerBudgetMin: mode === 'range' ? min : null } as Partial<EditableContact>)
      );
    } else {
      setContactForm(
        (prev) =>
          ({
            ...prev,
            customerBudgetRent: (max ?? undefined) as number | undefined,
            customerBudgetRentMin: mode === 'range' ? min : null
          } as Partial<EditableContact>)
      );
    }
  }, []);

  // ── property types: ONE control — the grouped dropdown, with up to 3
  //    recommendations pinned at the top of its own menu (badged "המלצה") and
  //    repeated in their taxonomy group below. There is no separate chip row:
  //    the Select's `renderValue` already renders every selected type as a
  //    deletable chip, so nothing is selected-but-invisible.
  //    The old "selected first" merge is gone with the chip row — it existed
  //    only so a pick couldn't be squeezed out of 3 chip slots; a pinned list
  //    that echoed the user's own selections back as "המלצה" would be a lie.
  const suggestedPropertyTypes = useMemo(() => {
    const smart = smartDefaults?.propertyTypes ?? [];
    const base = smart.length >= 3 ? smart : STATIC_POPULAR_PROPERTY_TYPES;
    return base.filter((p) => propertyTypes.includes(p)).slice(0, 3);
  }, [smartDefaults?.propertyTypes, propertyTypes]);
  // The dropdown offers the whole catalog PLUS anything already stored that the
  // catalog doesn't know about (legacy value) — without this the Select would
  // hold an out-of-range value that can't be unselected.
  const propertyTypeOptions = useMemo(
    () => [...propertyTypes, ...interestedInPropertyTypes.filter((p) => !propertyTypes.includes(p))],
    [propertyTypes, interestedInPropertyTypes]
  );

  // ── areas suggestions ──
  const areaSuggestions = useMemo(
    () =>
      (smartDefaults?.areaLocations ?? [])
        .filter((loc) => !interestedInLocations.some((l) => l.city === loc.city && l.neighborhood === loc.neighborhood))
        .slice(0, 6),
    [smartDefaults?.areaLocations, interestedInLocations]
  );
  const addSuggestedArea = useCallback((loc: ProcessedListingAddress): void => {
    setInterestedInLocations((prev) =>
      prev.some((l) => l.city === loc.city && l.neighborhood === loc.neighborhood) ? prev : [...prev, loc]
    );
  }, []);

  // ── lead source: a managed dropdown (no free text), plus a "type your own"
  //    row. Which rows exist — and how a stored value the presets don't contain
  //    is folded in — is `buildContactOriginOptions`, shared with the CRM
  //    table's in-cell editor (`CrmCellControls.CellOrigin`) so the two lists
  //    cannot drift. It also resolves the "בוט Webot" label collision this
  //    block used to render as two identical rows. ──
  const contactOrigin = contactForm.contactOrigin ?? '';
  const originOptions = useMemo(() => buildContactOriginOptions(contactOrigin), [contactOrigin]);
  const commitCustomSource = useCallback((): void => {
    const v = customSourceDraft.trim();
    if (v) setFormValue('contactOrigin', v);
    setCustomSourceOpen(false);
    setCustomSourceDraft('');
  }, [customSourceDraft, setFormValue]);

  // ── completeness meter (out of 5, mirrors the mock) ──
  const completeness = useMemo(() => {
    let done = 0;
    if (phoneNumber?.number) done += 1;
    if (fullName) done += 1;
    if (selectedCustomerTypes.length) done += 1;
    if (isBuyer || isTenant) {
      if (saleBudget || rentBudget) done += 1;
      if (interestedInLocations.length) done += 1;
    } else if (isSeller || isLandlord) {
      done += sellingListings.length || rentingOutListings.length ? 2 : 0;
    }
    return Math.min(done, 5);
  }, [phoneNumber?.number, fullName, selectedCustomerTypes.length, isBuyer, isTenant, isSeller, isLandlord, saleBudget, rentBudget, interestedInLocations.length, sellingListings.length, rentingOutListings.length]);

  // ── validation ──
  // Two tiers, deliberately. DISPLAY invalidity (mailInvalid / tehudatZehutInvalid /
  // the per-row additional-phone check) turns a field red wherever it is visible.
  // BLOCKING invalidity is what disables saving — and in edit mode a stored legacy
  // value the user did NOT touch never blocks: the dialog hydrates years-old contacts
  // (pre-validation imports, AI-stamped fields) whose email / ת"ז / extra phones can
  // be invalid, and those fields sit inside the collapsed "פרטים נוספים" section, so
  // gating on them read as "the save button is randomly not clickable". Same
  // principle as the primary-phone rule below, extended to every validated field:
  // only what the user actually changed is gated.
  const mailInvalid = !!contactForm.mail?.trim() && !isValidEmail(contactForm.mail);
  const tehudatZehutInvalid = !!contactForm.tehudatZehut?.trim() && !isValidIsraeliId(contactForm.tehudatZehut);
  // Primary phone is strictly validated only where it's TYPED (create). In edit
  // mode it's the locked doc key — legacy lenient-era numbers must not block
  // saving other fields.
  const primaryPhoneInvalid = !editMode && !bulkEdit && !!(callerPhoneNumber ?? '').trim() && !isStrictValidPhone(callerPhoneNumber as string);
  const validationBaseline = editMode ? baselineContactRef.current : null;
  const unchangedFromBaseline = (current: string | null | undefined, stored: string | null | undefined): boolean =>
    validationBaseline !== null && (current ?? '') === (stored ?? '');
  const baselineAdditionalPhones = validationBaseline?.additionalPhoneNumbers ?? [];
  // Every field below lives inside the collapsible "פרטים נוספים" section.
  const collapsedFieldBlocks =
    (mailInvalid && !unchangedFromBaseline(contactForm.mail, validationBaseline?.mail)) ||
    (tehudatZehutInvalid && !unchangedFromBaseline(contactForm.tehudatZehut, validationBaseline?.tehudatZehut)) ||
    (contactForm.additionalPhoneNumbers ?? []).some(
      (p) => !!p.trim() && !isStrictValidPhone(p) && !baselineAdditionalPhones.includes(p)
    );
  const hasFieldErrors = collapsedFieldBlocks || primaryPhoneInvalid;

  // A blocking error must never hide behind the collapsed section: if the user types
  // an invalid value and then closes "פרטים נוספים", the disabled save button would
  // have no visible reason. One-shot on the transition — the user may close it again.
  useEffect(() => {
    if (collapsedFieldBlocks) setMoreDetailsOpen(true);
  }, [collapsedFieldBlocks]);

  // ── save ──
  const listingIdsOf = (listings: ProcessedListingData[]): string[] => listings.map((l) => l.id).filter((id): id is string => Boolean(id));

  // ── dirty check + guarded close ──
  // Serializes the live form state and the state hydrated from the baseline
  // contact the same way, so an untouched dialog compares equal. The owner
  // listing pickers are compared by id, but only once their async hydration
  // finished — before that the empty picker vs. stored ids is loading, not an
  // edit.
  const computeIsDirty = (): boolean => {
    const snapshotOf = (
      form: unknown,
      tagList: string[],
      locations: ProcessedListingAddress[],
      types: string[]
    ): string => JSON.stringify({ form, tagList, locations, types });
    const baseline = baselineContactRef.current;
    if (
      snapshotOf(contactForm, tags, interestedInLocations, interestedInPropertyTypes) !==
      snapshotOf(
        baseline ?? {},
        asArray<string>(baseline?.tags),
        asArray<ProcessedListingAddress>(baseline?.interestedInLocations),
        asArray<string>(baseline?.interestedInPropertyTypes)
      )
    ) {
      return true;
    }
    if (!bulkEdit && !loadingContactListings) {
      if (isSeller && JSON.stringify(listingIdsOf(sellingListings)) !== JSON.stringify(baseline?.sellingListingIds ?? [])) return true;
      if (isLandlord && JSON.stringify(listingIdsOf(rentingOutListings)) !== JSON.stringify(baseline?.rentingOutListingIds ?? [])) return true;
    }
    return false;
  };

  // Every user-initiated close (backdrop, Escape, the X, the cancel button)
  // goes through here; programmatic closes after a successful save call the
  // raw onClose directly.
  const requestClose = (): void => {
    if (loading) return;
    if (computeIsDirty()) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };
  // Each listings field is only written when its group is active, so removing the
  // seller/landlord type (or editing an unrelated field) never wipes stored ids.
  const buildPayload = (): Partial<RealEstateContact> => ({
    ...contactForm,
    tags,
    interestedInLocations,
    interestedInPropertyTypes,
    additionalPhoneNumbers: (contactForm.additionalPhoneNumbers ?? []).map((p) => p.trim()).filter(Boolean),
    ...(isSeller && !bulkEdit ? { sellingListingIds: listingIdsOf(sellingListings) } : {}),
    ...(isLandlord && !bulkEdit ? { rentingOutListingIds: listingIdsOf(rentingOutListings) } : {})
  });

  // ── the EDIT payload: a DIFF against the hydrated contact, never the whole form ──
  //
  // `buildPayload()` spreads `contactForm`, and `contactForm` is seeded from the WHOLE
  // hydrated Firestore document (`setContactForm(contact)`) — `EditableContact` is a no-op
  // alias for `RealEstateContact`, since its `Omit` key resolves to `never`. So the payload
  // is ~60 fields, of which the user typically touched one. Sending it whole broke in three
  // separate ways, and diffing closes all three at once:
  //
  //  1. THE WRITE IS REJECTED. `POST /api/v1/contacts/update` refuses an unknown key with a
  //     400 rather than dropping it, and the hydrated document carries ~18 keys it will
  //     never accept: `path` (added by the Firestore converter on read — and, until the
  //     write moved server-side, written BACK into the document as a literal field, because
  //     the direct `update()` bypassed the converter that strips it), the Cloud-Function-
  //     derived names and distinct-location arrays, the legacy `customerType` /
  //     `customerSeriousness` mirrors, the bulk-messaging and AI pipeline stamps, and the
  //     deprecated `street` / `neighborhood` pair.
  //  2. LEGACY DATA FAILS VALIDATION ON FIELDS NOBODY TOUCHED. This dialog routinely opens
  //     years-old imports: a 6,000-character `additionalNotes`, a `crmStatus` outside the
  //     current enum, an `interestedInLocations` entry with no city. Re-sending them makes
  //     the save fail over data the realtor cannot even see, which is the same failure the
  //     two-tier validation above was built to avoid.
  //  3. IT IS EXPENSIVE. Six Cloud Functions fire on a contact write; the server drops
  //     no-op keys, but only after it has been handed them.
  //
  // Compared per key with a deep equal, because half these fields are arrays and objects
  // (`tags`, `interestedInLocations`, the amenity bag's siblings) where identity says
  // nothing. Only keys the payload HAS are considered — a field the payload omits (the
  // seller listing ids when the seller type is off) must stay untouched, not be cleared.
  const buildEditDiff = (): Partial<RealEstateContact> => {
    const { id, ...payload } = buildPayload();
    const baseline = baselineContactRef.current;
    // No baseline means nothing to diff against — send everything and let the server's
    // whitelist decide. Unreachable in practice: edit mode implies a hydrated contact.
    if (!baseline) return payload;

    const diff: Record<string, unknown> = {};
    Object.entries(payload).forEach(([key, value]) => {
      if (DERIVED_FIELDS_NEVER_SENT.has(key)) return;
      if (!isEqual(value, (baseline as Record<string, unknown>)[key])) diff[key] = value;
    });
    return diff as Partial<RealEstateContact>;
  };

  /**
   * The CREATE body for `POST /api/v1/contacts/create`.
   *
   * The same filter `buildEditDiff` applies, minus the diff — on a create the form starts
   * empty (`resetForNext` sets `{}`) or from a host's SEED, and there is no baseline to
   * compare against. A seed may carry more than the realtor typed (the call row's carries the
   * raw AI analysis), which is why the server-stamped fields are in the never-sent list too.
   *
   * Three exclusions, and each one would otherwise 400 the whole create rather than be
   * quietly dropped — the endpoint REJECTS an unknown key by design:
   *
   *  - `DERIVED_FIELDS_NEVER_SENT` — the Cloud-Function-stamped names and distinct-location
   *    arrays plus the legacy `customerType` / `customerSeriousness` mirrors, all of which
   *    the server derives from the fields below.
   *  - `id` and `callerPhoneNumber` — the document id IS the phone, and the server seeds the
   *    field from the id it normalized. A value sent from here could only disagree with it.
   *  - `undefined` / `null` / `''` — an untouched control, not a deliberate clear. On an
   *    EDIT an explicit null is how a field is cleared, which is why that path keeps them;
   *    on a create there is nothing to clear, and `''` in a numeric field is exactly what
   *    `contacts-data-hygiene.md` exists to keep out of the corpus.
   */
  const buildCreateFields = (): Record<string, unknown> => {
    const { id, ...payload } = buildPayload();
    const fields: Record<string, unknown> = {};
    Object.entries(payload).forEach(([key, value]) => {
      if (DERIVED_FIELDS_NEVER_SENT.has(key) || key === 'callerPhoneNumber') return;
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value) && value.length === 0) return;
      fields[key] = value;
    });
    return fields;
  };

  const resetForNext = (): void => {
    setContactForm({} as EditableContact);
    setTags([]);
    setInterestedInLocations([]);
    setInterestedInPropertyTypes([]);
    setSellingListings([]);
    setRentingOutListings([]);
    setSaleBudgetMode('exact');
    setRentBudgetMode('exact');
    setMoreDetailsOpen(false);
  };

  // ── the SERVER bulk patch: what `/contacts/bulk-update` will actually receive ──
  //
  // Two filters, and both are load-bearing:
  //
  //  1. DERIVED FIELDS ARE REMOVED. `handleCustomerTypesChange` writes `customerType`
  //     alongside `customerTypes` (via `buildCustomerTypesUpdate`) so this form's own
  //     reads stay coherent — and `customerType` is one of the two legacy mirrors the
  //     endpoint REFUSES, with a 400 for the whole request rather than a silent drop,
  //     because `updateContactOfUser` derives it from the real document. Picking a
  //     customer type is the single most common bulk edit there is, so leaving it in
  //     fails on the first realistic use. Same list and same reason as `buildEditDiff`.
  //  2. EMPTY VALUES ARE DROPPED, mirroring `stripUnwritableBulkValues` server-side. An
  //     empty array or an empty/whitespace string is what an UNTOUCHED field looks like,
  //     and this one patch lands on up to 2,000 contacts: writing one would wipe that
  //     field on every one of them, with no undo outside the audit ledger. `false`, `0`
  //     and an explicit `null` are values a realtor chose and pass through — that is how
  //     "unmark hot" and "clear the status" work at all.
  //
  // Stripping HERE as well as there is what lets the review panel and the save button
  // agree with the write: a form filled with nothing but blanks shows an empty review and
  // a disabled button, instead of a 400 arriving after the realtor pressed confirm.
  //
  // ⚠️ `contactForm` starts EMPTY in bulk (there is no `contact` to hydrate from), so a
  // key is present below only because the realtor touched the control that sets it. THAT
  // is where "only what was filled" comes from; the three guards handle the fields kept
  // in their own state, and nothing else needs a guard.
  const serverBulkPatch = useMemo(() => {
    const source: Record<string, unknown> = { ...(contactForm as Record<string, unknown>) };
    delete source.id;
    if (tags.length) source.tags = tags;
    if (interestedInLocations.length) source.interestedInLocations = interestedInLocations;
    if (interestedInPropertyTypes.length) source.interestedInPropertyTypes = interestedInPropertyTypes;
    // The caller's own bulk-only controls, folded in BEFORE the strip so they obey exactly
    // the same rules — including the derived-field filter, so a caller cannot smuggle a
    // `customerType` past it.
    Object.assign(source, bulkExtraPatch ?? {});

    const patch: Record<string, unknown> = {};
    Object.entries(source).forEach(([key, value]) => {
      if (DERIVED_FIELDS_NEVER_SENT.has(key)) return;
      if (value === undefined) return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'string' && value.trim().length === 0) return;
      patch[key] = value;
    });
    return patch;
  }, [contactForm, tags, interestedInLocations, interestedInPropertyTypes, bulkExtraPatch]);

  const bulkWritesNothing = Object.keys(serverBulkPatch).length === 0;

  /**
   * Puts ONE bulk field back to untouched — what a deletable review chip calls.
   *
   * Deleting the key rather than writing a neutral value is the whole point: `undefined`
   * is the only state this patch reads as "leave it alone", and every "empty" value a
   * control could produce means something else (`null` clears, `[]` is stripped but still
   * re-armed by the next render of a non-empty control).
   *
   * `customerType` rides along with `customerTypes` because `getContactCustomerTypes`
   * reads BOTH — clearing only the canonical field would leave the dropdown still showing
   * a selection that no longer reaches the wire. `crmSubStatus` rides along with
   * `crmStatus` for the same coherence reason in the other direction: a stage without a
   * status is not a state the picker can produce. The reverse is NOT coupled — "set
   * בטיפול, don't touch the stage" is a legitimate patch.
   */
  const resetBulkField = useCallback((key: string): void => {
    if (key === 'tags') {
      setTags([]);
      return;
    }
    if (key === 'interestedInLocations') {
      setInterestedInLocations([]);
      return;
    }
    if (key === 'interestedInPropertyTypes') {
      setInterestedInPropertyTypes([]);
      return;
    }
    const companions = key === 'customerTypes' ? ['customerType'] : key === 'crmStatus' ? ['crmSubStatus'] : [];
    setContactForm((prev) => {
      const next = { ...(prev as Record<string, unknown>) };
      delete next[key];
      companions.forEach((companion) => delete next[companion]);
      return next as Partial<EditableContact>;
    });
  }, []);

  const submitServerBulk = async (): Promise<void> => {
    if (!onBulkSubmit || bulkWritesNothing) return;
    setLoading(true);
    setSaveError(null);
    try {
      await onBulkSubmit(serverBulkPatch);
      resetForNext();
      onClose();
    } catch {
      // Deliberately swallowed, and the dialog deliberately STAYS OPEN.
      //
      // A bulk refusal is not one error. The agency cap says "select fewer"; a drift
      // refusal says "the list changed, look again" and must NOT be retried with the same
      // stale count; a 403 names a permission. Three different next moves, and only the
      // caller can tell them apart — it holds the server envelope, and
      // `describeCrmBulkFailure` is the single place that decides which is which. It
      // renders that sentence through `renderBulkFooter`, beside the button that produced
      // it. Setting `saveError` here would stack a second, vaguer message underneath.
    } finally {
      setLoading(false);
    }
  };

  const bulkEditContacts = async (): Promise<void> => {
    if (!selected) return;
    setLoading(true);
    try {
      // Bulk semantics (unchanged from the previous form): only ADD/SET what was
      // actually filled — empty tags/locations/types never clear existing values.
      const { id, ...rest } = contactForm;
      if (tags.length) rest.tags = tags;
      if (interestedInLocations.length) rest.interestedInLocations = interestedInLocations;
      if (interestedInPropertyTypes.length) rest.interestedInPropertyTypes = interestedInPropertyTypes;
      if (!rest.customerTypes?.length) {
        delete (rest as Record<string, unknown>).customerTypes;
        delete (rest as Record<string, unknown>).customerType;
      }
      const contactsToUpdate = new Map();
      for (const contactPhoneNumber of selected) {
        contactsToUpdate.set(doc(db, 'dashboard-users-v2', email, 'contacts', contactPhoneNumber), rest);
      }
      await batchUpdate(contactsToUpdate);
      resetForNext();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const submit = async (): Promise<void> => {
    // Checked BEFORE `bulkEdit`: `serverBulk` implies it, and the legacy branch below
    // writes Firestore directly from the browser — the one thing the CRM page must never
    // reach (see the `onBulkSubmit` prop docs).
    if (serverBulk) {
      await submitServerBulk();
      return;
    }
    if (bulkEdit) {
      await bulkEditContacts();
      return;
    }
    // !phoneNumber gates CREATE only (it becomes the new doc id). In edit mode the
    // id is already locked, and a legacy stored phone that no longer parses must
    // not turn the save button into a silent no-op.
    if (hasFieldErrors || isDuplicatePhone || (!editMode && !phoneNumber)) return;
    setLoading(true);
    setSaveError(null);
    try {
      if (editMode) {
        const contactId = contactForm.id as string;
        const changed = buildEditDiff();
        // EDIT — never `updateContact`. That one writes `set(merge: true)`, which on a
        // document the signed-in user does not own does not fail: it MINTS one in
        // their own book. And it resolves the contact's prior state from `allContacts`,
        // which on the CRM page is empty and everywhere else lands asynchronously — so
        // it takes its create branch and derives the legacy `customerSeriousness`
        // mirror from a null prior state. The server edit needs no prior state from
        // here: it reads the document.
        //
        // THE DIALOG DOES NOT CLOSE OPTIMISTICALLY, and that is deliberate: its entire
        // error surface — `saveError` and the "not in your book" branch below — lives
        // INSIDE this dialog, so closing before the write resolves would leave a failed
        // save with nowhere to report itself. "Reflect immediately" is satisfied by the
        // list and the side panel, which patch optimistically and reconcile from the
        // Firestore listener; the modal's job here is to be the place the failure can
        // still be seen.
        let blockedListKept = false;
        if (Object.keys(changed).length > 0) {
          const result = await updateContact(contactId, changed);
          // The blocked list can overrule an untick of "ביקש לא לקבל דיוור": a row the flag did
          // not create (a human's הסר, a Meta verdict, a block placed on the blocked page) stands
          // and the flag is true again server-side. The save succeeded — so the dialog closes as
          // usual — but the realtor has to be told, or the box they just cleared is ticked again
          // on the next open with no explanation. The blocked page is where such a row is lifted.
          if (result && result.blockedList?.kept) {
            blockedListKept = true;
            enqueueSnackbar(result.blockedList.message ?? 'החסימה לדיוור נשארה בתוקף — ניתן להסיר אותה רק ממסך "חסומים לדיוור".', {
              variant: 'warning',
              autoHideDuration: 8000
            });
          }
        }
        onClose();
        // What was actually PERSISTED, not the whole form: the form carries derived and
        // rejected fields that were never written, and handing those back invites a
        // caller to render a contact that does not exist in the database.
        if (onSuccess) {
          onSuccess({
            ...baselineContactRef.current,
            ...changed,
            ...(blockedListKept ? { optedOutOfBulkMessages: true } : {}),
            id: contactId
          });
        }
        return;
      }
      if (!phoneNumber) return; // narrowed by the guard above; repeated for TS
      const parsedPhoneNumber = phoneNumber.number as string;
      // CREATE — through the SERVER, not `updateContact`.
      //
      // `useLegacyContactWrites().updateContact` writes `set(merge: true)` straight from the browser, which
      // gets four things wrong at once: it silently OVERWRITES a contact already at that
      // number instead of refusing, applies no field whitelist, leaves no audit row, and —
      // the one customers actually reported — cannot refresh the Postgres mirror the CRM
      // page reads. A browser write reaches Postgres only through the Cloud Function's queue
      // entry and the once-a-minute drain, so the contact the realtor had just created was
      // missing from the contacts page for up to 60 seconds. The endpoint drains its own key
      // inline, so it is there by the time this promise resolves.
      const fields = buildCreateFields();
      const created = await createContact({
        contactId: parsedPhoneNumber,
        fields,
        defaultContactOrigin: MANUAL_CONTACT_ORIGIN
      });
      // The server NORMALIZES the phone, so the id it filed the contact under is the one to
      // hand on — `onSuccess` opens a card by it, and opening the un-normalized form would
      // 404 on a contact that was created perfectly well.
      const createdContact: Partial<RealEstateContact> = {
        ...fields,
        contactOrigin: (fields.contactOrigin as string) ?? MANUAL_CONTACT_ORIGIN,
        id: created.contactId,
        callerPhoneNumber: created.contactId
      };
      resetForNext();
      // THE POOLED BOOK (plan D4): the office already had this person, so nothing was
      // created — the caller was put on the record's list and gets the office's card. A
      // success, said plainly, and the flow goes on exactly as a create would: the card
      // that opens is the office's one record, which is the point.
      if (created.alreadyInOffice) {
        enqueueSnackbar('הלקוח כבר היה ב"לקוחות המשרד". הוספנו אותו ל"הלקוחות שלי".', { variant: 'info', autoHideDuration: 6000 });
      }
      if (showCelebrationOnCreate && !created.alreadyInOffice) {
        setCelebrateContact(createdContact);
        return;
      }
      onClose();
      if (onSuccess) onSuccess(createdContact);
    } catch (error) {
      // Previously there was no catch at all: a failed save cleared the spinner and
      // left the dialog open with no explanation, which reads as "nothing happened".
      console.error('Failed saving contact', error);
      // A 409 is the CREATE's own failure and gets its own copy: "try again" is actively
      // wrong advice for it — retrying produces the same refusal — and the realtor needs to
      // know the number is taken rather than that the save broke. The server's Hebrew
      // message distinguishes a live twin from a soft-deleted one, so prefer it.
      const duplicate = isContactDuplicateError(error);
      setSaveError(
        duplicate
          ? error.serverMessage ?? 'כבר קיים לקוח עם מספר הטלפון הזה.'
          : // The agency's switch into the pooled book is running: the server's own retry
          // sentence, never "the save broke" (plan D4, decision 4).
          isContactBookBusyError(error)
          ? error.serverMessage ?? 'מאגר המשרד בתהליך איחוד, נסו שוב בעוד כמה דקות'
          : isContactNotInYourBookError(error)
          ? CONTACT_NOT_IN_YOUR_BOOK_MESSAGE
          : // The server REFUSED the form over one field (a 400): say which, by the label the
            // form shows for it. "Try again" is the wrong advice for a refusal — the same form
            // is refused again — and it is what had a realtor press save eleven times in two
            // minutes (2026-10-12). Null for every other failure, which keeps the copy below.
            describeContactValidationFailure(error, translate) ??
            translate('contactsPage.error.failedToSaveContact', {
              defaultValue: 'שמירת הלקוח נכשלה. נסו שוב.'
            })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    void submit();
  };

  // ── phone change (re-key) completion ──
  // The whole form — contactForm, the baseline, the dirty guard — is keyed to the OLD
  // doc id, which the server just hard-deleted. Keeping the dialog open would offer a
  // save button over a document that no longer exists (every save 404s), so it closes
  // via the raw onClose (the programmatic-close convention: no dirty nag on a change
  // that already persisted) and lets the host reopen from fresh data if it wants to.
  const handlePhoneChangeCompleted = (result: REST_schema.ChangeContactPhoneResponse): void => {
    setChangePhoneOpen(false);
    onClose();
    onPhoneChanged?.(result.oldContactId, result);
  };

  // ── budget block ──
  const renderBudgetBlock = (market: 'sale' | 'rent'): JSX.Element => {
    const rent = market === 'rent';
    const mode = rent ? rentBudgetMode : saleBudgetMode;
    const setMode = rent ? setRentBudgetMode : setSaleBudgetMode;
    const maxVal = rent ? rentBudget : saleBudget;
    const minVal = rent ? rentBudgetMin : saleBudgetMin;
    const maxField = rent ? 'customerBudgetRent' : 'customerBudget';
    const minField = rent ? 'customerBudgetRentMin' : 'customerBudgetMin';
    const step = rent ? RENT_STEP : SALE_STEP;
    const fallback = rent ? DEFAULT_RENT_RANGE : DEFAULT_SALE_RANGE;
    const smart = rent ? smartDefaults?.rentBudget : smartDefaults?.saleBudget;
    const suggested = smart?.suggested ?? fallback;
    const presets = smart?.presets ?? [];

    // Display values fall back to the suggestion until the user commits something.
    const displayMax = maxVal ?? suggested.max;
    const displayMin = mode === 'range' ? minVal ?? suggested.min : null;
    const sliderMax = Math.max(rent ? 15_000 : 6_000_000, Math.ceil((displayMax * 1.25) / step) * step);

    const commitSlider = (_e: unknown, value: number | number[]): void => {
      if (Array.isArray(value)) {
        const [lo, hi] = value[0] <= value[1] ? value : [value[1], value[0]];
        applyBudget(market, 'range', lo, hi);
      } else {
        applyBudget(market, 'exact', null, value);
      }
    };

    const priceEditable = (field: string, value: number | null, placeholder: number, isMin: boolean): JSX.Element =>
      editingPrice === field ? (
        <TextField
          autoFocus
          size="small"
          value={value ?? ''}
          InputProps={{
            inputComponent: CustomNumericFormat as never,
            startAdornment: <Box sx={{ mr: 0.5, color: 'text.disabled', fontWeight: 700 }}>₪</Box>
          }}
          // RTL ordering: digits right-aligned next to the ₪ adornment (which
          // sits at the inline-start, i.e. the right edge, under the RTL theme).
          inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
          onChange={({ target }) => {
            const n = target.value ? +target.value : null;
            if (isMin) applyBudget(market, 'range', n, maxVal ?? placeholder);
            else applyBudget(market, mode, mode === 'range' ? displayMin : null, n);
          }}
          onBlur={() => setEditingPrice(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') setEditingPrice(null);
          }}
          sx={{ width: 130 }}
        />
      ) : (
        <ButtonBase
          onClick={() => setEditingPrice(field)}
          sx={{
            borderRadius: 1,
            px: 1,
            py: 0.25,
            typography: 'h6',
            fontWeight: 800,
            color: value == null ? 'text.disabled' : 'text.primary',
            '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08), color: 'secondary.dark' }
          }}
        >
          <span dir="ltr">{formatBudgetShort(value ?? placeholder, rent)}</span>
          <EditIcon sx={{ fontSize: 13, ml: 0.5, color: 'text.disabled' }} />
        </ButtonBase>
      );

    return (
      <Box key={market}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <ContactTableAiFieldFlag id={maxField as never} contact={contact}>
            <Typography sx={{ ...blockLabelSx, mb: 0 }}>
              {rent
                ? translate('contactsPage.newContact.v2.rentBudget', { defaultValue: 'תקציב שכירות (לחודש)' })
                : translate('contactsPage.newContact.v2.budget', { defaultValue: 'תקציב' })}
            </Typography>
          </ContactTableAiFieldFlag>
          <Box sx={{ display: 'inline-flex', bgcolor: alpha(theme.palette.grey[500], 0.14), borderRadius: '10px', p: '3px' }}>
            {(['range', 'exact'] as const).map((m) => (
              <ButtonBase
                key={m}
                onClick={() => {
                  setMode(m);
                  if (m === 'exact') applyBudget(market, 'exact', null, maxVal);
                  else applyBudget(market, 'range', minVal ?? suggested.min, maxVal ?? suggested.max);
                }}
                sx={{
                  px: 1.75,
                  py: 0.5,
                  borderRadius: '8px',
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: mode === m ? 'secondary.dark' : 'text.secondary',
                  bgcolor: mode === m ? 'background.paper' : 'transparent',
                  boxShadow: mode === m ? `0 2px 6px ${alpha(theme.palette.grey[500], 0.3)}` : 'none'
                }}
              >
                {m === 'range'
                  ? translate('contactsPage.newContact.v2.range', { defaultValue: 'טווח' })
                  : translate('contactsPage.newContact.v2.exact', { defaultValue: 'מדויק' })}
              </ButtonBase>
            ))}
          </Box>
        </Stack>

        {/* RTL-aware slider (MUI flips it automatically under the RTL theme: low on the right).
            No horizontal padding — the track must share the same left/right edge as every
            other section's fields (the thumb's slight overhang at the extremes is fine). */}
        <Box sx={{ pt: 1 }}>
          <Slider
            value={mode === 'range' ? [displayMin ?? suggested.min, displayMax] : displayMax}
            min={0}
            max={sliderMax}
            step={step}
            disabled={loading}
            onChange={commitSlider}
            sx={{
              '& .MuiSlider-track': { background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`, border: 'none' },
              '& .MuiSlider-thumb': {
                width: 22,
                height: 22,
                bgcolor: 'background.paper',
                border: '3px solid',
                borderColor: 'primary.main',
                '&:nth-of-type(even)': { borderColor: 'secondary.main' }
              }
            }}
          />
        </Box>

        {/* dir="rtl" pins the visual order regardless of how the ambient flex
            direction resolves: the MIN price renders FIRST (rightmost), then
            the dash, then the max — the RTL reading order of a range. */}
        <Stack dir="rtl" direction="row" alignItems="center" justifyContent="center" spacing={1}>
          {mode === 'range' && (
            <>
              {priceEditable(minField, displayMin, suggested.min, true)}
              <Typography sx={{ color: 'text.disabled', fontWeight: 700 }}>–</Typography>
            </>
          )}
          {priceEditable(maxField, maxVal, suggested.max, false)}
        </Stack>

        {presets.length > 0 && (
          <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ gap: 0.75, mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 700 }}>
              {translate('contactsPage.newContact.recommendation', { defaultValue: '✦ המלצה' })}
            </Typography>
            {presets.slice(0, 4).map((p, i) => (
              <Chip
                key={i}
                size="small"
                clickable
                variant="outlined"
                color="primary"
                label={formatBudgetRangeShortRtl(p.min, p.max, rent)}
                onClick={() => {
                  setMode('range');
                  applyBudget(market, 'range', p.min, p.max);
                }}
              />
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  // ── owner (seller/landlord) listing block ──
  const renderOwnerListings = (kind: TransactionType): JSX.Element => {
    const rent = kind === 'rent';
    return (
      <Box key={kind}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <Typography sx={{ ...blockLabelSx, mb: 0 }}>
            {rent
              ? translate('contactsPage.newContact.v2.listingForRent', { defaultValue: 'הנכס להשכרה' })
              : translate('contactsPage.newContact.v2.listingForSale', { defaultValue: 'הנכס למכירה' })}
          </Typography>
          <Button size="small" variant="text" startIcon={<AddIcon />} disabled={loading} onClick={() => setListingDialogFor(kind)} sx={{ fontWeight: 700 }}>
            {translate('signatures.newSignatureForm.createListing')}
          </Button>
        </Stack>
        <NewListingPicker
          loading={loading || loadingContactListings}
          disabled={loading}
          filters={rent ? 'listingListedFor:"rent"' : 'NOT listingListedFor:"rent"'}
          selectedListings={rent ? rentingOutListings : sellingListings}
          onSelectedListings={rent ? setRentingOutListings : setSellingListings}
        />
      </Box>
    );
  };

  // ── customer-type dropdown items ──
  const renderTypeMenuItem = ({ type, Icon, accent }: TypeOptionConfig): JSX.Element => {
    const on = selectedCustomerTypes.includes(type);
    return (
      <MenuItem key={type} value={type} sx={{ py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%' }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(accent, 0.12),
              color: accent,
              flex: 'none'
            }}
          >
            <Icon sx={{ fontSize: 17 }} />
          </Box>
          <Typography component="span" sx={{ fontSize: 14, fontWeight: on ? 700 : 500, flex: 1 }}>
            {getTypeLabel(type)}
          </Typography>
          {on && <Check sx={{ fontSize: 16, color: 'primary.main' }} />}
        </Stack>
      </MenuItem>
    );
  };

  const renderTypeValueChips = (selected: unknown): JSX.Element => {
    const types = selected as CustomerType[];
    if (!types.length) {
      return (
        <Typography component="span" sx={{ color: 'text.disabled', fontSize: 14 }}>
          {translate('contactsPage.newContact.v2.pickCustomerType', { defaultValue: 'בחרו סוג לקוח…' })}
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {types.map((type) => {
          const config = TYPE_OPTION_BY_TYPE.get(type);
          const TypeIcon = config?.Icon;
          const accent = config?.accent ?? theme.palette.grey[600];
          return (
            <Chip
              key={type}
              size="small"
              icon={TypeIcon ? <TypeIcon sx={{ fontSize: 15 }} /> : undefined}
              label={getTypeLabel(type)}
              sx={{ color: accent, bgcolor: alpha(accent, 0.1), fontWeight: 700, '& .MuiChip-icon': { color: accent } }}
            />
          );
        })}
      </Box>
    );
  };

  const dialogTitle = customTitle
    ? customTitle
    : bulkEdit
    ? (translate('contactsPage.newContact.bulkEdit') as string)
    : editMode
    ? fullName || (translate('contactsPage.newContact.edit') as string)
    : fullName || (translate('contactsPage.newContact.new') as string);

  // "ונמצא לו התאמות" is a promise the matching engine only keeps for the side that
  // is LOOKING — it matches a buyer's or a tenant's criteria against listings. A
  // מוכר/משכיר has no criteria to match, so on an owner-only contact the line named
  // the one thing this form was NOT going to do, right at the top of a dialog opened
  // from "save this owner" on the board. Owners get what their form actually
  // delivers: the property attached to the person.
  const ownerOnly = !bulkEdit && (isSeller || isLandlord) && !isBuyer && !isTenant;
  const dialogSubtitle = bulkEdit
    ? (translate('contactsPage.newContact.v2.bulkSubtitle', {
        count: bulkAudienceCount,
        defaultValue: `העריכה תחול על ${bulkAudienceCount.toLocaleString('en-US')} לקוחות`
      }) as string)
    : ownerOnly
    ? (translate('contactsPage.newContact.v2.subtitleOwner', { defaultValue: 'כמה פרטים — והנכס שלו נשמר מקושר אליו ✦' }) as string)
    : (translate('contactsPage.newContact.v2.subtitle', { defaultValue: 'כמה פרטים — ונמצא לו התאמות ✦' }) as string);

  // The avatar square is 52px and used to hold `selected.length`, which on
  // /dashboard/contacts is a page's worth of ticked rows. A CRM filter selection is
  // routinely four digits ("8,102"), which overflows the square — so past a thousand the
  // badge goes compact and the exact number stays in the subtitle beside it.
  const bulkBadgeLabel = bulkAudienceCount >= 1000 ? `${Math.floor(bulkAudienceCount / 100) / 10}K` : String(bulkAudienceCount);

  return (
    <Dialog
      fullWidth
      fullScreen={isMobile}
      open={open}
      maxWidth="md"
      onClose={requestClose}
      sx={zIndex ? { zIndex } : undefined}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '22px' } }}
    >
      <style>{`
        @keyframes v2-bump { 0% { transform: scale(1); } 45% { transform: scale(1.14) rotate(-3deg); } 100% { transform: scale(1); } }
        @keyframes v2-pop { from { opacity: 0; transform: translateY(10px) scale(.97); } to { opacity: 1; transform: none; } }
      `}</style>

      <FormControl component="form" onSubmit={handleSubmit} sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* live header: avatar + name + close */}
        <Stack direction="row" alignItems="center" spacing={1.75} sx={{ px: 3, pt: 2.25 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: bulkEdit ? (bulkBadgeLabel.length > 3 ? 16 : 22) : initials ? 20 : 26,
              fontWeight: 800,
              color: bulkEdit || initials ? '#fff' : 'text.disabled',
              background:
                bulkEdit || initials ? `linear-gradient(135deg, ${theme.palette.secondary.main}, #6c8bff)` : alpha(theme.palette.grey[500], 0.16),
              boxShadow: bulkEdit || initials ? `0 8px 18px ${alpha(theme.palette.secondary.main, 0.28)}` : 'none',
              animation: avatarBump ? 'v2-bump .3s cubic-bezier(.16,1,.3,1)' : 'none',
              transition: 'background .3s',
              flex: 'none'
            }}
          >
            {bulkEdit ? bulkBadgeLabel : initials || '＋'}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 21, fontWeight: 800, lineHeight: 1.2 }}>
              {dialogTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {dialogSubtitle}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={requestClose} sx={{ bgcolor: alpha(theme.palette.grey[500], 0.12), alignSelf: 'flex-start' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        {/* completeness meter */}
        {!bulkEdit && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, pt: 1.75, pb: 1.75 }}>
            <Box sx={{ flex: 1, height: 7, borderRadius: 4, bgcolor: alpha(theme.palette.grey[500], 0.22), overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${(completeness / 5) * 100}%`,
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                  transition: 'width .55s cubic-bezier(.16,1,.3,1)'
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              <Box component="b" sx={{ color: 'primary.dark' }}>
                {completeness}
              </Box>
              /5
            </Typography>
          </Stack>
        )}

        {/* the header region is fixed while the body scrolls — give it a real
            edge, mirroring the DialogActions rule at the bottom */}
        <Divider sx={{ mt: bulkEdit ? 2.25 : 0 }} />

        <DialogContent sx={{ pt: 2.5, pb: 3, px: 3 }}>
          <Stack spacing={3}>
            {/* Guidance the realtor has to read BEFORE filling anything in — chiefly that
                this form REPLACES what it writes. Above the first field on purpose. */}
            {bulkIntro}

            {/* ── section: contact details (hidden in bulk) ── */}
            {!bulkEdit && (
              <Box>
                <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>
                  {translate('contactsPage.newContact.v2.contactDetailsTitle', { defaultValue: 'פרטי קשר' })}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <ContactDialogInputPhoneNumber
                      label={translate('contactsPage.tableColumns.callerPhoneNumber') as string}
                      /* The doc id in edit mode, the typed value in create — see the
                         derivation above. `?? ''` matters: the input's label shrink is
                         `value !== ""`, so an `undefined` here floats the label over an
                         empty box, which is exactly what the missing-phone bug looked
                         like. */
                      value={(callerPhoneNumber ?? '') as string}
                      loading={loading}
                      disabled={editMode}
                      phoneNumber={phoneNumber}
                      onChange={handleChange}
                    />
                    {/* The phone is the DOC ID, so edit mode locks the field — changing it
                        is a server-side re-key with its own consequences (everything
                        attached moves, old links die), which is why it gets its own
                        deliberate flow instead of an editable input.
                        `allowPhoneChange` withholds it entirely when the host knows the
                        re-key would be refused (a colleague-owned CRM row). */}
                    {editMode && allowPhoneChange && typeof contactForm.id === 'string' && !!contactForm.id && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                        disabled={loading}
                        onClick={() => setChangePhoneOpen(true)}
                        sx={{ mt: 0.5, px: 0.75, fontWeight: 700 }}
                      >
                        {translate('contactsPage.newContact.v2.changePhone', { defaultValue: 'עדכון מספר' })}
                      </Button>
                    )}
                    {/* The Yad2-mask caveat, under the field it is about. The board
                        surfaces flag the number with a ⚠ beside it, which is the right
                        place to NOTICE it and the wrong place to fix it — here the
                        field is still editable and what she types is what gets saved
                        and linked. Rendered as plain caption text rather than an Alert:
                        it is a caveat about a value, not a failure, and an Alert block
                        under the first field reads as "the form is broken". */}
                    {showMaskedPhoneCaveat && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}>
                        {translate('contactsPage.newContact.v2.maskedPhoneCaveat', {
                          defaultValue: 'המספר נראה כמספר וירטואלי של יד2, ולא הקו האישי של בעל הנכס. אפשר לתקן אותו כאן, והמספר שיישמר הוא מה שמופיע בשדה.'
                        })}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ContactTableAiFieldFlag id="callerFirstName" contact={contact}>
                      <TextField
                        fullWidth
                        name="callerFirstName"
                        label={translate('contactsPage.tableColumns.callerFirstName')}
                        value={contactForm.callerFirstName ?? ''}
                        disabled={loading}
                        onChange={handleChange}
                      />
                    </ContactTableAiFieldFlag>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ContactTableAiFieldFlag id="callerLastName" contact={contact}>
                      <TextField
                        fullWidth
                        name="callerLastName"
                        label={translate('contactsPage.tableColumns.callerLastName')}
                        value={contactForm.callerLastName ?? ''}
                        disabled={loading}
                        onChange={handleChange}
                      />
                    </ContactTableAiFieldFlag>
                  </Grid>
                </Grid>
                <ContactPhoneDuplicateAlert
                  isDuplicatePhone={isDuplicatePhone}
                  existingName={existingContactForPhone?.contactFullName || existingContactForPhone?.callerFirstName || ''}
                  collidingHandler={collidingHandler}
                  onEditExisting={handleEditExisting}
                  existsLabel={translate('contactsPage.newContact.contactExists')}
                  editInsteadLabel={translate('contactsPage.newContact.editInstead')}
                />
                <Button size="small" variant="text" onClick={() => setMoreDetailsOpen((v) => !v)} sx={{ mt: 0.75, fontWeight: 700, px: 0 }}>
                  {moreDetailsOpen
                    ? `− ${translate('contactsPage.newContact.v2.lessDetails', { defaultValue: 'פחות פרטים' })}`
                    : `+ ${translate('contactsPage.newContact.v2.moreDetails', { defaultValue: 'פרטים נוספים' })}`}
                </Button>
                <Collapse in={moreDetailsOpen}>
                  <Grid container spacing={1.5} sx={{ pt: 1 }}>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        name="mail"
                        type="email"
                        label={translate('contactsPage.tableColumns.mail')}
                        value={contactForm.mail ?? ''}
                        disabled={loading}
                        error={mailInvalid}
                        helperText={mailInvalid ? translate('contactsPage.error.invalidEmail') : ''}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        name="tehudatZehut"
                        label={translate('contactsPage.tableColumns.tehudatZehut')}
                        value={contactForm.tehudatZehut ?? ''}
                        disabled={loading}
                        error={tehudatZehutInvalid}
                        helperText={tehudatZehutInvalid ? translate('contactsPage.error.invalidTz') : ''}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="address"
                        label={translate('contactsPage.tableColumns.address')}
                        value={contactForm.address ?? ''}
                        disabled={loading}
                        onChange={handleChange}
                      />
                    </Grid>
                    {/* Additional phone numbers — array editor: one validated row per number */}
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <Typography sx={{ ...blockLabelSx, mb: 0 }}>{translate('contactsPage.tableColumns.additionalPhoneNumbers')}</Typography>
                        {(contactForm.additionalPhoneNumbers ?? []).map((phone, index) => {
                          const rowInvalid = !!phone.trim() && !isStrictValidPhone(phone);
                          return (
                            <Stack key={index} direction="row" spacing={1} alignItems="flex-start" sx={{ maxWidth: { xs: '100%', sm: 320 } }}>
                              <TextField
                                fullWidth
                                size="small"
                                type="tel"
                                placeholder={translate('contactsPage.tableColumns.callerPhoneNumber') as string}
                                value={phone}
                                disabled={loading}
                                error={rowInvalid}
                                helperText={rowInvalid ? translate('contactsPage.error.invalidPhoneNumber') : ''}
                                onChange={(e) => updateAdditionalPhone(index, e.target.value)}
                              />
                              <IconButton size="small" disabled={loading} onClick={() => removeAdditionalPhone(index)} sx={{ mt: 0.5 }}>
                                <Close fontSize="small" />
                              </IconButton>
                            </Stack>
                          );
                        })}
                        <Button size="small" variant="text" startIcon={<AddIcon />} disabled={loading} onClick={addAdditionalPhone} sx={{ alignSelf: 'flex-start' }}>
                          {translate('contactsPage.newContact.addPhone')}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Collapse>
              </Box>
            )}

            {/* ── tags (first-class, with a leading tag icon) ── */}
            <MemoTagsAutocomplete
              selectedTags={tags}
              setSelectedTags={setTags}
              disabled={loading}
              editable
              icon={<LocalOffer sx={{ fontSize: 18, color: 'text.disabled', mx: 0.5 }} />}
            />

            {/* "who is this person" → "how do we classify them". The one genuine
                break inside the body; rendered unconditionally because tags show
                in bulk edit too. */}
            <Divider />

            {/* ── customer type — a multi-select dropdown that CLOSES after each
                   pick (reopen to pick more). The 4 market roles lead with their
                   icons; the professional roles sit at the end under "אחרים". ── */}
            <Box>
              <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>
                {translate('contactsPage.newContact.v2.customerType', { defaultValue: 'סוג לקוח' })}{' '}
                <Box component="small" sx={{ fontWeight: 500, color: 'text.disabled' }}>
                  {translate('contactsPage.newContact.v2.canPickMultiple', { defaultValue: 'אפשר לבחור כמה' })}
                </Box>
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={selectedCustomerTypes}
                disabled={loading}
                onChange={(e) => handleCustomerTypesChange(e.target.value as unknown as CustomerType[])}
                // Compact by default; widens when several types are picked so
                // the selection chips get room instead of wrapping to rows.
                sx={{ maxWidth: { md: selectedCustomerTypes.length > 1 ? 460 : 300 }, transition: 'max-width .25s ease' }}
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  open: typeMenuOpen,
                  onOpen: () => setTypeMenuOpen(true),
                  onClose: () => setTypeMenuOpen(false),
                  renderValue: renderTypeValueChips,
                  MenuProps: { PaperProps: { sx: { maxHeight: 420 } } }
                }}
              >
                {MAIN_TYPE_OPTIONS.map(renderTypeMenuItem)}
                <ListSubheader disableSticky sx={{ lineHeight: '30px', fontSize: 11, fontWeight: 700 }}>
                  {translate('contactsPage.newContact.v2.otherTypes', { defaultValue: 'אחרים' })}
                </ListSubheader>
                {OTHER_TYPE_OPTIONS.map(renderTypeMenuItem)}
              </TextField>
            </Box>

            {/* ── status + hot — always visible, first-class ── */}
            <CrmStatusHotPicker
              crmStatus={contactForm.crmStatus ?? null}
              crmSubStatus={contactForm.crmSubStatus ?? null}
              crmIsHot={!!contactForm.crmIsHot}
              sides={crmSides}
              loading={loading}
              onChangeStatus={handleChangeCrmStatus}
              onToggleHot={handleToggleCrmHot}
            />

            {/* ── seeker profile (buyer / tenant) ── */}
            <Collapse in={seekerActive} unmountOnExit>
              <Stack spacing={2.75} sx={{ animation: 'v2-pop .35s cubic-bezier(.16,1,.3,1)' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ p: 1.5, borderRadius: '14px', background: `linear-gradient(90deg, ${alpha(seekerAccent, 0.08)}, transparent)` }}
                >
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 21, bgcolor: alpha(seekerAccent, 0.15) }}>
                    {seekerEmoji}
                  </Box>
                  <Box>
                    <Typography sx={sectionLabelSx}>{seekerTitle}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {translate('contactsPage.newContact.v2.whatAreTheyLookingFor', { defaultValue: 'מה מחפשים?' })}
                    </Typography>
                  </Box>
                </Stack>

                {/* areas — the first thing a seeker profile asks for (deliberately
                    ahead of budget/rooms: it's the highest-signal matching field) */}
                <Box>
                  <Typography sx={blockLabelSx}>{translate('contactsPage.newContact.v2.areas', { defaultValue: 'אזורים מבוקשים' })}</Typography>
                  {/* Streets are offered here. A street-level interested area is a
                      first-class pick — `isAddressStreetAnInterestedLocation` matches
                      it exactly, narrowed to that street — and every other picker
                      writing `interestedInLocations` (realtor settings, lead item,
                      import, listings-match) already offers them. Suppressing them
                      here made a realtor's search for רחוב חורגין in רמת גן look like
                      the field had hung, since the rows were fetched and then dropped
                      (reported 2026-09-02). */}
                  <MemoLocationPicker
                    maxPicks={MAX_INTERESTED_IN_LOCATIONS}
                    disabled={loading}
                    pickedLocations={interestedInLocations}
                    searchSeed={areaSearchSeed ?? undefined}
                    onLocationsChange={handleInterestedInLocationsChange}
                  />
                  {unresolvedLocations.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ gap: 0.75, mt: 1 }}>
                      <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 700 }}>
                        {translate('contactsPage.newContact.v2.unresolvedAreas', { defaultValue: 'לא זוהו מהשיחה — לחצו לתיקון:' })}
                      </Typography>
                      {unresolvedLocations.map((text) => (
                        <Chip
                          key={text}
                          size="small"
                          clickable
                          variant="outlined"
                          color="warning"
                          label={text}
                          title={translate('contactsPage.newContact.v2.unresolvedAreaHint', {
                            defaultValue: 'לחיצה תפתח חיפוש עם הטקסט; ✕ מסיר בלי לבחור'
                          })}
                          disabled={loading}
                          onClick={() => startFixingUnresolvedLocation(text)}
                          onDelete={() => removeUnresolvedLocation(text)}
                        />
                      ))}
                    </Stack>
                  )}
                  {areaSuggestions.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ gap: 0.75, mt: 1 }}>
                      <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 700 }}>
                        {translate('contactsPage.newContact.recommendation', { defaultValue: '✦ המלצה' })}
                      </Typography>
                      {areaSuggestions.map((loc, i) => (
                        <Chip
                          key={i}
                          size="small"
                          clickable
                          variant="outlined"
                          color="primary"
                          icon={<AddIcon />}
                          label={loc.neighborhood || loc.city}
                          onClick={() => addSuggestedArea(loc)}
                          sx={{ borderStyle: 'dashed' }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                {showSaleBudget && renderBudgetBlock('sale')}
                {showRentBudget && renderBudgetBlock('rent')}

                {/* property types: a single grouped dropdown (דירות / צמודי קרקע
                    / מסחרי / אחר), directly under the budget. Up to 3
                    recommendations are pinned at the top of its menu under
                    "המלצות" — and repeated in their own group below — instead of
                    living in a separate chip row. Every selected type still
                    renders as a deletable chip in the closed field, so nothing
                    can be selected-but-invisible. */}
                <Box>
                  <Typography sx={blockLabelSx}>{translate('listingsMatch.filters.propertyTypesTitle')}</Typography>
                  <PropertyTypeSelect
                    small
                    disabled={loading}
                    options={propertyTypeOptions}
                    recommendedOptions={suggestedPropertyTypes}
                    value={interestedInPropertyTypes}
                    onChange={setInterestedInPropertyTypes}
                    label={translate('contactsPage.newContact.v2.propertyTypesSelected', { defaultValue: 'סוגי נכס שנבחרו' }) as string}
                    emptyLabel={translate('contactsPage.newContact.v2.propertyTypesPick', { defaultValue: 'בחרו מהרשימה המלאה…' }) as string}
                  />
                </Box>

                {/* property requirements — labeled min/max fields, two columns on
                    desktop. A CSS grid (real `gap`) rather than MUI <Grid>: Grid
                    implements spacing as item padding + a negative container
                    margin, and under the RTL style flip inside this flex Stack
                    the compensation lands unevenly — the whole block rendered
                    indented from the right edge of the form. */}
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                  <RangeSelectPair
                    label={translate('contactsPage.newContact.v2.rooms', { defaultValue: 'חדרים' }) as string}
                    options={roomsScale as readonly number[]}
                    formatOption={(v) => (v === 6 ? '6+' : String(v))}
                    minValue={(contactForm.roomsMin as number | null) ?? null}
                    maxValue={(contactForm.roomsMax as number | null) ?? null}
                    disabled={loading}
                    onChangeMin={(v) => setRangeBound('roomsMin', 'roomsMax', 'min', v)}
                    onChangeMax={(v) => setRangeBound('roomsMin', 'roomsMax', 'max', v)}
                  />
                  <Box>
                    <RangeSelectPair
                      label={translate('contactsPage.newContact.v2.floor', { defaultValue: 'קומה' }) as string}
                      options={DIALOG_FLOOR_SCALE}
                      formatOption={floorLabel}
                      minValue={contactForm.floorsMin ?? null}
                      maxValue={contactForm.floorsMax ?? null}
                      disabled={loading}
                      onChangeMin={(v) => setRangeBound('floorsMin', 'floorsMax', 'min', v)}
                      onChangeMax={(v) => setRangeBound('floorsMin', 'floorsMax', 'max', v)}
                    />
                    <FormControlLabel
                      sx={{ mt: 0.25, mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={!!contactForm.excludeTopFloor}
                          disabled={loading}
                          onChange={() => setFormValue('excludeTopFloor', !contactForm.excludeTopFloor)}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 13 }}>
                          {translate('contactsPage.newContact.v2.excludeTopFloor', { defaultValue: 'לא קומה אחרונה' })}
                        </Typography>
                      }
                    />
                  </Box>
                  <RangeNumericPair
                    label={translate('contactsPage.newContact.v2.builtArea', { defaultValue: 'שטח בנוי (מ״ר)' }) as string}
                    // Says what the field DOES, because for two months it did nothing: listing
                    // matching reads it now, and a realtor who learned to skip it has no other
                    // way to find that out from inside the form.
                    hint={translate('contactsPage.newContact.v2.builtAreaHint', { defaultValue: 'משפיע על התאמת נכסים' }) as string}
                    minValue={contactForm.squareMeterMin ?? null}
                    maxValue={contactForm.squareMeterMax ?? null}
                    disabled={loading}
                    onChangeMin={(v) => setFormValue('squareMeterMin', v)}
                    onChangeMax={(v) => setFormValue('squareMeterMax', v)}
                    onBlur={() => normalizeRange('squareMeterMin', 'squareMeterMax')}
                  />
                  <RangeNumericPair
                    label={translate('contactsPage.newContact.v2.plotSize', { defaultValue: 'שטח מגרש (מ״ר)' }) as string}
                    hint={translate('contactsPage.newContact.v2.plotSizeHint', { defaultValue: 'רלוונטי לצמודי קרקע' }) as string}
                    minValue={contactForm.plotSizeMin ?? null}
                    maxValue={contactForm.plotSizeMax ?? null}
                    disabled={loading}
                    onChangeMin={(v) => setFormValue('plotSizeMin', v)}
                    onChangeMax={(v) => setFormValue('plotSizeMax', v)}
                    onBlur={() => normalizeRange('plotSizeMin', 'plotSizeMax')}
                  />
                  {/* No parking-count picker here: parking is the "חניה" amenity chip
                      below (`hasParking`), which is what matching, the Matches tab
                      search and listing alerts read. The old count (`parkingSpacesCount`)
                      was read by nothing, so a realtor could set it and filter on nothing. */}
                  <Box>
                    <Typography sx={blockLabelSx}>{translate('contactsPage.newContact.v2.windDirections', { defaultValue: 'כיווני אוויר' })}</Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={contactForm.windDirectionsCount ?? ''}
                      disabled={loading}
                      onChange={(e) => setFormValue('windDirectionsCount', e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <MenuItem value="">
                        <em>{translate('contactsPage.newContact.v2.noRequirement', { defaultValue: 'ללא דרישה' })}</em>
                      </MenuItem>
                      {[1, 2, 3, 4].map((v) => (
                        <MenuItem key={v} value={v}>
                          {v}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>

                {/* amenities */}
                <Box>
                  <Typography sx={blockLabelSx}>{translate('contactsPage.newContact.sections.amenities')}</Typography>
                  <ListingBooleanPropertiesDetailsBag contact data={contactForm} loading={loading} onChangePropertyDetails={handleChangePropertyDetails} />
                </Box>
              </Stack>
            </Collapse>

            {/* ── owner profile (seller / landlord) ── */}
            <Collapse in={ownerActive} unmountOnExit>
              <Stack spacing={2.5} sx={{ animation: 'v2-pop .35s cubic-bezier(.16,1,.3,1)' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ p: 1.5, borderRadius: '14px', background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)}, transparent)` }}
                >
                  <Box
                    sx={{ width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 21, bgcolor: alpha(theme.palette.primary.main, 0.15) }}
                  >
                    {ownerEmoji}
                  </Box>
                  <Box>
                    <Typography sx={sectionLabelSx}>{ownerTitle}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {translate('contactsPage.newContact.v2.pickOrCreateListing', { defaultValue: 'בוחרים נכס קיים או יוצרים חדש — בלי להקליד כתובת ידנית' })}
                    </Typography>
                  </Box>
                </Stack>
                {isSeller && renderOwnerListings('sale')}
                {isLandlord && renderOwnerListings('rent')}
              </Stack>
            </Collapse>

            {/* ── lead source (not in bulk) — a managed dropdown of distinct values,
                   with typing as the deliberate exception. Provenance has to stay
                   countable, so Yad2 and Madlan (etc.) are separate presets rather
                   than typed-in variants; "מקור אחר…" exists for the source no preset
                   can name, and sits at the TOP so reaching it is not a scroll past
                   ten wrong answers. Same list and same order as the CRM table's
                   in-cell picker — both read `buildContactOriginOptions`. ── */}
            {!bulkEdit && <Divider />}
            {!bulkEdit && (
              <Box>
                <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>
                  {translate('contactsPage.newContact.v2.leadSourceTitle', { defaultValue: 'מקור הליד' })}
                </Typography>
                <TextField
                  select
                  size="small"
                  value={originOptions.includes(contactOrigin) ? contactOrigin : ''}
                  disabled={loading}
                  onChange={(e) => {
                    if (e.target.value === CUSTOM_CONTACT_ORIGIN_VALUE) {
                      setCustomSourceOpen(true);
                      return;
                    }
                    setCustomSourceOpen(false);
                    setFormValue('contactOrigin', e.target.value);
                  }}
                  sx={{ width: { xs: '100%', md: 320 } }}
                  SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 340 } } } }}
                >
                  {/* The empty row stays first — `displayEmpty` needs it, and it is the
                      placeholder rather than a choice. "מקור אחר…" is the first actual
                      SUGGESTION, above the presets, matching the CRM table's picker: ten
                      presets with the custom row buried underneath reads as a closed
                      vocabulary with a footnote, and a realtor whose source is one
                      specific exhibition scrolls past ten wrong answers to reach it. The
                      divider moved with it, or it would separate the last preset from
                      nothing. */}
                  <MenuItem value="">
                    <em>{translate('contactsPage.newContact.v2.leadSourcePick', { defaultValue: 'בחרו מקור…' })}</em>
                  </MenuItem>
                  <MenuItem value={CUSTOM_CONTACT_ORIGIN_VALUE}>
                    ➕ {translate('contactsPage.newContact.v2.customSource', { defaultValue: 'מקור אחר…' })}
                  </MenuItem>
                  <Divider />
                  {originOptions.map((src) => (
                    <MenuItem key={src} value={src}>
                      {getContactOriginLabel(src)}
                    </MenuItem>
                  ))}
                </TextField>
                <Collapse in={customSourceOpen}>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, maxWidth: { md: 320 } }}>
                    <TextField
                      autoFocus={customSourceOpen}
                      fullWidth
                      size="small"
                      placeholder={translate('contactsPage.newContact.v2.customSourcePlaceholder', { defaultValue: 'שם המקור (למשל: תערוכת נדל״ן)…' }) as string}
                      value={customSourceDraft}
                      disabled={loading}
                      onChange={(e) => setCustomSourceDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitCustomSource();
                        }
                      }}
                    />
                    <Button variant="outlined" disabled={loading} onClick={commitCustomSource} sx={{ fontWeight: 700, flexShrink: 0 }}>
                      {translate('contactsPage.newContact.v2.add', { defaultValue: 'הוספה' })}
                    </Button>
                  </Stack>
                </Collapse>
              </Box>
            )}

            {/* ── opted out of bulk messages (ported from the old form's yes/no
                   select — a checkbox here). Not in bulk edit on purpose: the
                   bulk semantics are "only SET what was filled", and a tri-state
                   "leave unchanged" can't be expressed by a checkbox — a stray
                   toggle would silently clear individual opt-outs. */}
            {!bulkEdit && (
              <FormControlLabel
                sx={{ mr: 0, alignSelf: 'flex-start' }}
                control={
                  <Checkbox
                    size="small"
                    checked={contactForm.optedOutOfBulkMessages === true}
                    disabled={loading}
                    onChange={(e) => setFormValue('optedOutOfBulkMessages', e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {translate('contactsPage.tableColumns.optedOutOfBulkMessages', { defaultValue: 'ביקש לא לקבל דיוור' })}
                  </Typography>
                }
              />
            )}

            {/* ── free-text notes — the same additionalNotes the overview's פרטים
                   panel shows as "על הלקוח". Not in bulk edit: one shared note
                   spread onto many contacts would overwrite each one's own. ── */}
            {!bulkEdit && <Divider />}
            {!bulkEdit && (
              <Box>
                <Box sx={{ mb: 1.25 }}>
                  <ContactTableAiFieldFlag id="additionalNotes" contact={contact}>
                    <Typography sx={{ ...sectionLabelSx, mb: 0 }}>
                      {translate('contactsPage.newContact.v2.aboutClientTitle', { defaultValue: 'על הלקוח' })}
                    </Typography>
                  </ContactTableAiFieldFlag>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={8}
                  name="additionalNotes"
                  placeholder={translate('contactsPage.newContact.v2.aboutClientPlaceholder', { defaultValue: 'הוסיפו מידע חופשי על הלקוח…' }) as string}
                  value={contactForm.additionalNotes ?? ''}
                  disabled={loading}
                  onChange={handleChange}
                />
              </Box>
            )}

            {/* Bulk-only controls, supplied by the caller. They live at the END of the
                body — after the `!bulkEdit` blocks above, which are exactly the fields
                they replace — so a caller that can express "leave unchanged" may put
                them back in the place the single-contact form keeps them. */}
            {bulkExtraFields}
          </Stack>
        </DialogContent>

        {/* PINNED, not appended to the body: this is the last thing between the realtor
            and a write that lands on thousands of contacts, so it must be on screen no
            matter where the (long) form is scrolled to. */}
        {renderBulkFooter?.(serverBulkPatch, resetBulkField)}

        {/* Save failures used to be invisible: `submit` had no catch, so the spinner
            stopped and the dialog simply stayed open. Sits directly above the actions,
            i.e. next to the button that just failed. */}
        {saveError && (
          <Alert severity="error" sx={{ mx: 3, mb: 0 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="text" color="inherit" onClick={requestClose} sx={{ color: 'text.secondary', fontWeight: 700 }}>
            {customCancelButtonText ?? translate('contactsPage.control.cancel')}
          </Button>
          <Box sx={{ flex: 1 }} />
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={loading}
            /* The server-bulk branch has its own two reasons to refuse, and neither
               applies to the other two modes: a patch that writes NOTHING (the endpoint
               400s on one, so disabling says the same thing before the round trip), and
               whatever the caller is blocking on — the agency's cap, or a drift refusal
               that must not be retried with the same stale count. The legacy expression is
               reproduced verbatim for every other caller. */
            disabled={
              serverBulk ? bulkWritesNothing || !!bulkSaveDisabled : !bulkEdit && (hasFieldErrors || isDuplicatePhone)
            }
            startIcon={<Check />}
            /* ── THE LABEL SURVIVES THE SAVE ────────────────────────────────────────
               Reported as "when saving, the text on the save button disappears".

               It is MUI's default and it is not subtle: `loadingPosition` defaults to
               'center' (LoadingButton.js:105), and the center branch paints the ROOT
               `color: transparent` while loading (:56-63) — which takes the label AND
               the ✓ with it, since an SvgIcon is `fill: currentColor`. The spinner is
               visible only because it re-declares its own colour.

               'start' is the house style here — 15 buttons already use its 'end' twin —
               and it is the correct half of it: the position REQUIRES a matching icon
               (a dev-only propTypes error otherwise) and this button has one. The check
               fades out under `.startIconLoadingStart { opacity: 0 }` and the spinner
               takes its place, so the button reads "saving" without going mute.

               NOTHING MOVES. Every box is identical across the three states — `color`
               and `opacity` are paint-only and the indicator is absolutely positioned —
               so the button does not resize when `loading` flips. */
            loadingPosition="start"
            sx={{
              fontWeight: 800,
              borderRadius: '12px',
              px: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, #1fc16b)`,
              boxShadow: `0 10px 22px -6px ${alpha(theme.palette.primary.main, 0.5)}`,
              /* RIDER 1 — the newly-visible label must not be GREY on the gradient.
                 `LoadingButton` passes `disabled: disabled || loading` (:119), so
                 `Mui-disabled` is on for the whole save and `Button`'s own rule paints
                 `action.disabled` at a specificity this sx would otherwise lose to. It
                 was invisible before precisely because the label was transparent. The
                 spinner inherits `currentColor` on this position, so one declaration
                 covers both.

                 ⚠️ SCOPED TO THE LOADING STATE, and it must be. `Mui-disabled` is ALSO on
                 whenever the form refuses to save — an invalid field, a duplicate phone —
                 and this button is disabled that way far more often than it is saving.
                 An unscoped rule would paint a REFUSING button in full white on the green
                 gradient, i.e. make the one state that must look unavailable look like
                 the primary action. The extra class is what separates "busy" from
                 "blocked", and it also wins the specificity contest against Button's own
                 disabled rule. */
              '&.MuiLoadingButton-loading.Mui-disabled': { color: '#fff' },
              /* RIDER 2 — and it must not JUMP. MUI drops the start indicator at a
                 hardcoded 14px, while `px: 3` plus the start icon's own −4/+8 margins
                 put the ✓ glyph's centre at ~30px — so the spinner would appear ~8px
                 inboard of the mark it replaces. 22 puts the 16px spinner's centre on
                 the same 30px.

                 `insetInlineStart`, never `left`: stylis-plugin-rtl mirrors the
                 physical property (that is how MUI's own `left: 14` lands correctly on
                 the right edge under RTL), so a `left` here would be flipped too and
                 the override would miss the rule it is aiming at. The logical property
                 is not rewritten and resolves to the same edge in both directions. */
              '& .MuiLoadingButton-loadingIndicator': { insetInlineStart: 22 }
            }}
          >
            {customOkButtonText ?? translate('contactsPage.control.save')}
          </LoadingButton>
        </DialogActions>
      </FormControl>

      {/* inline listing creation for the owner pickers (mirrors the signature send form) */}
      {listingDialogFor !== null && (
        <NewListingDialog
          open
          defaultTransactionType={listingDialogFor}
          onClose={() => setListingDialogFor(null)}
          onConfirm={() => setListingDialogFor(null)}
          onListingCreation={(_listingId, _isExclusivitySet, createdListing) => {
            if (createdListing) {
              const created = createdListing as ProcessedListingData;
              if (listingDialogFor === 'rent') setRentingOutListings((prev) => [...prev, created]);
              else setSellingListings((prev) => [...prev, created]);
            }
            setListingDialogFor(null);
          }}
        />
      )}

      {/* phone-number change (re-key) — mounted on demand; edit mode only. `+1` on the
          stacking override: a host that floats THIS dialog above the default modal
          z-index (the signatures side-panel passes 2001) needs the nested dialog above
          it, where the MUI default (1300) would land it underneath. */}
      {changePhoneOpen && typeof contactForm.id === 'string' && !!contactForm.id && (
        <ChangePhoneDialog
          open
          contactId={contactForm.id}
          contactName={fullName || undefined}
          zIndex={zIndex ? zIndex + 1 : undefined}
          onClose={() => setChangePhoneOpen(false)}
          onCompleted={handlePhoneChangeCompleted}
        />
      )}

      {/* dirty-close confirmation — shown instead of silently discarding edits */}
      <Dialog open={confirmDiscardOpen} maxWidth="xs" onClose={() => setConfirmDiscardOpen(false)} PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>
            {translate('contactsPage.newContact.v2.discardTitle', { defaultValue: 'יש שינויים שלא נשמרו' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('contactsPage.newContact.v2.discardBody', { defaultValue: 'סגירת החלון תבטל את השינויים שביצעתם.' })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="inherit"
            onClick={() => {
              setConfirmDiscardOpen(false);
              onClose();
            }}
            sx={{ color: 'text.secondary', fontWeight: 700 }}
          >
            {translate('contactsPage.newContact.v2.discardConfirm', { defaultValue: 'סגירה בלי לשמור' })}
          </Button>
          <Button variant="contained" onClick={() => setConfirmDiscardOpen(false)} sx={{ fontWeight: 700 }}>
            {translate('contactsPage.newContact.v2.discardCancel', { defaultValue: 'המשך עריכה' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* celebration after a standalone create */}
      <ContactCreatedCelebration
        open={!!celebrateContact}
        contact={celebrateContact}
        onAddAnother={() => setCelebrateContact(null)}
        onClose={() => {
          setCelebrateContact(null);
          onClose();
        }}
      />
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Status + hot-lead picker — first-class UX, visible regardless of customer type.
// One tap sets the lifecycle status; the "בטיפול" stages (per pipeline side) are
// offered as a second row of chips; the hot-lead row is a prominent toggle.
// ══════════════════════════════════════════════════════════════════════════════

const STATUS_COLOR_KEYS: Record<CrmStatus, 'info' | 'primary' | 'warning' | 'success' | 'error' | 'grey'> = {
  new: 'info',
  in_progress: 'primary',
  paused: 'warning',
  won: 'success',
  lost: 'grey',
  irrelevant: 'grey'
};

function CrmStatusHotPicker({
  crmStatus,
  crmSubStatus,
  crmIsHot,
  sides,
  loading,
  onChangeStatus,
  onToggleHot
}: {
  crmStatus: CrmStatus | null;
  crmSubStatus: string | null;
  crmIsHot: boolean;
  sides: CrmPipelineSide[];
  loading: boolean;
  onChangeStatus: (status: CrmStatus | null, subStatus: string | null) => void;
  onToggleHot: () => void;
}): JSX.Element {
  const { t: translate } = useTranslation();
  const theme = useTheme();

  const accentOf = (status: CrmStatus): string => {
    const key = STATUS_COLOR_KEYS[status];
    return key === 'grey' ? theme.palette.grey[600] : theme.palette[key].main;
  };

  // Without a customer type there's no side context — offer the combined
  // buyer+seller stages, grouped by side inside the menu.
  const sidesToRender: CrmPipelineSide[] = sides.length ? sides : ['buyer', 'seller'];
  const multiSide = sidesToRender.length > 1;
  const subDefs = getCrmSubStatusDefinitionsForSides(sidesToRender);
  const sideLabels: Record<CrmPipelineSide, string> = {
    buyer: translate('contactsPage.newContact.v2.buyerSide', { defaultValue: 'קונה' }) as string,
    seller: translate('contactsPage.newContact.v2.sellerSide', { defaultValue: 'מוכר' }) as string
  };

  // The Select's value encodes both axes: '' (none) / `s:<status>` / `sub:<id>`
  // (which implies בטיפול). A stored sub-status the current sides don't offer
  // (the customer type changed) falls back to plain בטיפול so the Select never
  // holds an out-of-range value.
  const subKnown = !!crmSubStatus && subDefs.some((d) => d.id === crmSubStatus);
  const value = crmStatus === 'in_progress' && subKnown ? `sub:${crmSubStatus}` : crmStatus ? `s:${crmStatus}` : '';
  const handleSelect = (raw: string): void => {
    if (!raw) {
      onChangeStatus(null, null);
      return;
    }
    if (raw.startsWith('sub:')) {
      onChangeStatus('in_progress', raw.slice(4));
      return;
    }
    onChangeStatus(raw.slice(2) as CrmStatus, null);
  };

  const statusRow = (status: CrmStatus): JSX.Element => (
    <Stack direction="row" alignItems="center" spacing={1}>
      <CrmStatusIcon iconKey={status} sx={{ fontSize: 16, color: accentOf(status) }} />
      <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
        {crmStatusLabels[status]}
      </Typography>
    </Stack>
  );

  // Select children must be a flat array (no fragments), so the menu is built
  // imperatively: every lifecycle status, with the בטיפול stages indented right
  // below it — grouped by side when the contact is on both sides.
  const menuItems: JSX.Element[] = [
    <MenuItem key="none" value="">
      <em>{translate('contactsPage.newContact.v2.noStatus', { defaultValue: 'ללא סטטוס' })}</em>
    </MenuItem>
  ];
  for (const status of crmStatuses) {
    menuItems.push(
      <MenuItem key={status} value={`s:${status}`}>
        {statusRow(status)}
      </MenuItem>
    );
    if (status !== 'in_progress') continue;
    for (const side of sidesToRender) {
      const defs = subDefs.filter((d) => d.side === side);
      if (!defs.length) continue;
      if (multiSide) {
        menuItems.push(
          <ListSubheader key={`side-${side}`} disableSticky sx={{ lineHeight: '30px', fontSize: 11, fontWeight: 700 }}>
            {`${crmStatusLabels.in_progress} — ${sideLabels[side]}`}
          </ListSubheader>
        );
      }
      for (const def of defs) {
        menuItems.push(
          <MenuItem key={`sub-${def.id}`} value={`sub:${def.id}`} sx={{ pl: 4.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CrmStatusIcon iconKey={def.icon} sx={{ fontSize: 15, color: 'primary.main' }} />
              <Typography component="span" sx={{ fontSize: 13.5 }}>
                {def.label}
              </Typography>
            </Stack>
          </MenuItem>
        );
      }
    }
  }

  const renderValue = (raw: unknown): JSX.Element => {
    const val = raw as string;
    if (!val) {
      return (
        <Typography component="span" sx={{ color: 'text.disabled', fontSize: 14 }}>
          {translate('contactsPage.newContact.v2.pickStatus', { defaultValue: 'בחרו סטטוס…' })}
        </Typography>
      );
    }
    if (val.startsWith('sub:')) {
      const def = subDefs.find((d) => d.id === val.slice(4));
      return (
        <Stack direction="row" alignItems="center" spacing={0.75} component="span">
          <CrmStatusIcon iconKey="in_progress" sx={{ fontSize: 16, color: accentOf('in_progress') }} />
          <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
            {crmStatusLabels.in_progress}
          </Typography>
          {def && (
            <Typography component="span" sx={{ fontSize: 13.5, color: 'text.secondary' }}>
              · {def.label}
            </Typography>
          )}
        </Stack>
      );
    }
    const status = val.slice(2) as CrmStatus;
    return (
      <Stack direction="row" alignItems="center" spacing={0.75} component="span">
        <CrmStatusIcon iconKey={status} sx={{ fontSize: 16, color: accentOf(status) }} />
        <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
          {crmStatusLabels[status]}
        </Typography>
      </Stack>
    );
  };

  return (
    <Box>
      <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
        {translate('contactsPage.newContact.v2.statusTitle', { defaultValue: 'סטטוס' })}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <TextField
          select
          size="small"
          value={value}
          disabled={loading}
          onChange={(e) => handleSelect(e.target.value)}
          sx={{ width: { xs: '100%', sm: 320 } }}
          SelectProps={{ displayEmpty: true, renderValue, MenuProps: { PaperProps: { sx: { maxHeight: 380 } } } }}
        >
          {menuItems}
        </TextField>

        <ButtonBase
          disabled={loading}
          onClick={onToggleHot}
          aria-pressed={crmIsHot}
          title={translate('contactsPage.newContact.v2.hotCaption', { defaultValue: 'יקפוץ לראש הרשימה ויקבל טיפול מועדף' }) as string}
          sx={{
            height: 36,
            px: 1.25,
            flex: 'none',
            borderRadius: '18px',
            border: '1px solid',
            borderColor: crmIsHot ? alpha(theme.palette.error.main, 0.5) : alpha(theme.palette.grey[500], 0.3),
            bgcolor: crmIsHot ? alpha(theme.palette.error.main, 0.08) : 'transparent',
            color: crmIsHot ? 'error.dark' : 'text.secondary',
            fontSize: 12.5,
            fontWeight: crmIsHot ? 700 : 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            transition: 'background .15s, color .15s, border-color .15s',
            '&:hover': { borderColor: alpha(theme.palette.error.main, 0.5) },
            fontFamily: 'inherit'
          }}
        >
          <CrmHotFireIcon isHot sx={{ fontSize: 15, opacity: crmIsHot ? 1 : 0.4 }} />
          {translate('contactsPage.newContact.v2.hotTitle', { defaultValue: 'ליד חם' })}
          {crmIsHot && <Check sx={{ fontSize: 14 }} />}
        </ButtonBase>
      </Stack>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Min/max field pairs — the professional replacement for the old pill spread
// (rooms) and +/- steppers (floors). Dropdown pairs for discrete scales,
// numeric pairs for free sqm values.
// ══════════════════════════════════════════════════════════════════════════════

const DIALOG_FLOOR_SCALE: readonly number[] = Array.from({ length: 42 }, (_, i) => i - 1); // -1 … 40

const floorLabel = (v: number): string => (v === -1 ? 'מרתף' : v === 0 ? 'קרקע' : String(v));

function RangeSelectPair({
  label,
  options,
  formatOption,
  minValue,
  maxValue,
  disabled,
  onChangeMin,
  onChangeMax
}: {
  label: string;
  options: readonly number[];
  formatOption?: (v: number) => string;
  minValue: number | null;
  maxValue: number | null;
  disabled: boolean;
  onChangeMin: (v: number | null) => void;
  onChangeMax: (v: number | null) => void;
}): JSX.Element {
  const { t: translate } = useTranslation();
  const fmt = formatOption ?? String;
  const boundSelect = (bound: 'min' | 'max'): JSX.Element => (
    <TextField
      select
      fullWidth
      size="small"
      label={
        bound === 'min'
          ? translate('contactsPage.newContact.v2.from', { defaultValue: 'מ־' })
          : translate('contactsPage.newContact.v2.to', { defaultValue: 'עד' })
      }
      value={(bound === 'min' ? minValue : maxValue) ?? ''}
      disabled={disabled}
      onChange={(e) => (bound === 'min' ? onChangeMin : onChangeMax)(e.target.value === '' ? null : Number(e.target.value))}
      SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
    >
      <MenuItem value="">
        <em>{translate('contactsPage.newContact.v2.noRequirement', { defaultValue: 'ללא דרישה' })}</em>
      </MenuItem>
      {options.map((v) => (
        <MenuItem key={v} value={v}>
          {fmt(v)}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Box>
      <Typography sx={blockLabelSx}>{label}</Typography>
      <Stack direction="row" spacing={1}>
        {boundSelect('min')}
        {boundSelect('max')}
      </Stack>
    </Box>
  );
}

function RangeNumericPair({
  label,
  hint,
  minValue,
  maxValue,
  disabled,
  onChangeMin,
  onChangeMax,
  onBlur
}: {
  label: string;
  hint?: string;
  minValue: number | null;
  maxValue: number | null;
  disabled: boolean;
  onChangeMin: (v: number | null) => void;
  onChangeMax: (v: number | null) => void;
  onBlur: () => void;
}): JSX.Element {
  const { t: translate } = useTranslation();
  const parse = (raw: string): number | null => {
    if (!raw.trim()) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const boundField = (bound: 'min' | 'max'): JSX.Element => (
    <TextField
      fullWidth
      size="small"
      type="number"
      inputProps={{ min: 0, inputMode: 'numeric' }}
      label={
        bound === 'min'
          ? translate('contactsPage.newContact.v2.from', { defaultValue: 'מ־' })
          : translate('contactsPage.newContact.v2.to', { defaultValue: 'עד' })
      }
      value={(bound === 'min' ? minValue : maxValue) ?? ''}
      disabled={disabled}
      onChange={(e) => (bound === 'min' ? onChangeMin : onChangeMax)(parse(e.target.value))}
      onBlur={onBlur}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Typography variant="caption" color="text.disabled">
              {translate('contactsPage.newContact.v2.sqm', { defaultValue: 'מ״ר' })}
            </Typography>
          </InputAdornment>
        )
      }}
    />
  );
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={0.75}>
        <Typography sx={blockLabelSx}>{label}</Typography>
        {hint && (
          <Typography variant="caption" color="text.disabled" sx={{ mb: 1 }}>
            {hint}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={1}>
        {boundField('min')}
        {boundField('max')}
      </Stack>
    </Box>
  );
}
